import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Check,
  X,
  ExternalLink,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  RotateCw,
  CheckCheck,
  Package,
  EyeOff,
  ScanLine,
  Sparkles,
  CloudUpload,
  Download,
  Unlock,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Barcode,
  Button,
  cn,
  ConfirmDialog,
  EmptyState,
  Gtin,
  HighlightedMatch,
  Loading,
  PageHeader,
  Panel,
  PanelBody,
  ProgressBar,
  SectionLabel,
  Segmented,
  Select,
} from '../ui/index.js';
import { isValidGtin, repairGtinCheckDigit } from '../lib/gtin.js';
import { formatContenance, parseContenance, sameContenance, type Contenance } from '@mapara/core';
import { money, playBeep, score, stamp, toCapitalizedCase } from '../lib/format.js';
import { WorkbenchCorrectionsPage } from './WorkbenchCorrectionsPage.js';
import { SubBrandPills } from '../components/SubBrandPills.js';

/** Per-brand counters for the workbench filter: X à traiter / Y traités. */
interface BrandStat {
  readonly brand: string;
  readonly key: string;
  readonly pending: number;
  readonly done: number;
  readonly bands: { readonly review: number; readonly auto: number; readonly none: number };
}

/** P3 push batch (WAL) — plan → approve → execute against the Woo staging. */
interface WcBatch {
  readonly id: number;
  readonly env: string;
  readonly status: string;
  readonly createdBy: string;
  readonly approvedBy: string | null;
  readonly total: number;
  readonly succeeded: number;
  readonly failed: number;
  readonly createdAt: string;
  readonly finishedAt: string | null;
  readonly changeCount: number;
}

interface Candidate {
  readonly candidateId: number;
  readonly gtinRaw: string | null;
  readonly nameRaw: string;
  readonly brandRaw: string | null;
  readonly priceHt: string | number | null;
  readonly priceTtc: string | number | null;
  readonly url: string | null;
  readonly image: string | null;
  readonly sizeValue: number | null;
  readonly sizeUnit: string | null;
  readonly sourceKey: string;
  readonly sourceLabel: string;
  readonly sourceTrust: number;
  readonly scoreTotal: string | number;
  readonly band: string;
}

interface QueueClaimInfo {
  readonly userId: number;
  readonly userName: string;
  readonly isMine: boolean;
  readonly expiresAt: string;
}

interface QueueItem {
  readonly productId: number;
  readonly band: string;
  readonly bestScore: string | number | null;
  readonly decided: string | null;
  readonly claim?: QueueClaimInfo | null;
  readonly product: {
    readonly id: number;
    readonly sku: string | null;
    readonly name: string;
    readonly brand: string | null;
    readonly barcodeRaw: string | null;
    readonly eanRaw: string | null;
    readonly regularPriceTtc: string | number | null;
    readonly images: readonly string[];
    readonly sizeValue: number | null;
    readonly sizeUnit: string | null;
    readonly wcProductId: number;
  };
  readonly candidates: readonly Candidate[];
}

interface QueueResponse {
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly bandTotals?: {
    readonly auto: number;
    readonly none: number;
    readonly review: number;
  };
  readonly items: readonly QueueItem[];
  readonly activeClaimsCount?: number;
  readonly claimExpiresAt?: string | null;
}

/** Structured size → parsed contenance, or null when the data is absent/garbled. */
const contenanceOf = (value: number | null, unit: string | null): Contenance | null =>
  value !== null && value > 0 && unit !== null && unit.trim() !== ''
    ? parseContenance(`${value} ${unit.trim()}`)
    : null;

interface DecisionItem {
  readonly id: number;
  readonly productId?: number;
  readonly gtin: string | null;
  readonly algoVersion: string;
  readonly decidedBy: string | null;
  readonly decidedAt: string | null;
  readonly isVisible?: boolean | null;
  readonly note?: string | null;
  readonly bestCandidate?: {
    readonly gtin: string;
    readonly confidence: number;
    readonly sourceId?: number;
  } | null;
  readonly product: {
    readonly sku: string | null;
    readonly name: string;
    readonly brand: string | null;
    readonly images: readonly string[];
    readonly purchasePriceHt: string | number | null;
    readonly barcodeRaw?: string | null;
    readonly eanRaw?: string | null;
  };
}

interface ScanLookupItem {
  readonly id: number;
  readonly sku: string | null;
  readonly name: string;
  readonly brand: string | null;
  readonly image: string | null;
  readonly barcodeRaw: string | null;
  readonly eanRaw: string | null;
  readonly gtinCanonical: string | null;
  readonly reviewStatus: 'approved' | 'rejected' | 'pending' | 'operator';
  readonly isApproved: boolean;
  readonly isRejected: boolean;
  readonly isDirectMatch: boolean;
}

interface ScanLookupResponse {
  readonly code: string;
  readonly rawCode: string;
  readonly isValid: boolean;
  readonly matchType: 'none' | 'single' | 'variant_family' | 'already_approved';
  readonly items: readonly ScanLookupItem[];
}

type View = 'queue' | 'corrections' | 'approved' | 'rejected' | 'pack' | 'hide' | 'images';

/** URL segments for the subpages under /workbench (operator-bookmarkable). */
const VIEW_SEGMENTS: Record<View, string> = {
  queue: '',
  corrections: 'corrections',
  approved: 'approved',
  rejected: 'rejected',
  pack: 'pack',
  hide: 'hide',
  images: 'images',
};

function viewFromSegment(segment: string | undefined): View {
  switch (segment) {
    case 'corrections':
      return 'corrections';
    case 'approved':
      return 'approved';
    case 'rejected':
      return 'rejected';
    case 'pack':
      return 'pack';
    case 'hide':
      return 'hide';
    case 'images':
      return 'images';
    default:
      return 'queue';
  }
}

// ApiError + fetchJson moved to lib/fetch-json (shared with WooEnginePage)
import { ApiError, fetchJson } from '../lib/fetch-json.js';

/** Modern keyboard badge. */
function Key({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  return (
    <kbd className="rounded border border-border-strong bg-cream px-1.5 py-0.5 font-mono text-[10px] font-semibold text-ink-soft shadow-xs">
      {children}
    </kbd>
  );
}

function cleanSourceLabel(label: string): string {
  return label.replace(/\s*\([^)]*\)/g, '').trim();
}

/**
 * Competitor websites sell at retail: their HT is a *selling* price, not our
 * purchase cost — label it `HT`, never `achat` (reserved for supplier lists
 * and B2B portals, the only purchase-price sources).
 */
const COMPETITOR_SOURCE_KEYS: ReadonlySet<string> = new Set([
  'pharma-shop',
  'parafendri',
  'parashop',
  'paraexpert',
]);

function digitsOf(code: string | null | undefined): string {
  return (code ?? '').replace(/\D/g, '');
}

/**
 * Returns the valid GTIN or the mathematically repaired GTIN if the check digit is off,
 * or raw digits if unrepairable.
 */
function effectiveGtin(code: string | null | undefined): string | null {
  if (!code) return null;
  const digits = code.replace(/\D/g, '');
  if (digits.length >= 8 && isValidGtin(digits)) return digits;
  return repairGtinCheckDigit(digits) ?? (digits.length >= 8 ? digits : null);
}

function candidatePriority(c: Candidate): number {
  const k = (c.sourceKey ?? '').toLowerCase();
  if (k.includes('pharma-shop') || k.includes('pharmashop')) return 1;
  if (k.includes('parashop')) return 2;
  return 3;
}

/**
 * The candidate approve should act on: one that CORROBORATES our current code
 * (keeps the site barcode, records the evidence), else the best barcode-bearing
 * one, strictly prioritizing pharma-shop > parashop > others.
 */
function chosenCandidate(item: QueueItem): Candidate | undefined {
  const our = digitsOf(item.product.barcodeRaw ?? item.product.eanRaw);
  const ourEff = effectiveGtin(our);
  if (our !== '') {
    const match = item.candidates.find((c) => {
      if (c.gtinRaw === null) return false;
      const cDigits = digitsOf(c.gtinRaw);
      return cDigits === our || (ourEff !== null && effectiveGtin(cDigits) === ourEff);
    });
    if (match !== undefined) return match;
  }
  // User rule: Always pharma-shop then parashop then others
  const withGtin = item.candidates
    .filter((c) => c.gtinRaw !== null)
    .sort((a, b) => {
      const pA = candidatePriority(a);
      const pB = candidatePriority(b);
      if (pA !== pB) return pA - pB;
      return Number(b.scoreTotal) - Number(a.scoreTotal);
    });
  if (withGtin.length > 0) return withGtin[0];

  const sortedAll = [...item.candidates].sort((a, b) => {
    const pA = candidatePriority(a);
    const pB = candidatePriority(b);
    if (pA !== pB) return pA - pB;
    return Number(b.scoreTotal) - Number(a.scoreTotal);
  });
  return sortedAll[0];
}

/**
 * S02 — GTIN Workbench:
 * Modern high-density workbench for catalog reconciliation:
 * thumbnails, corroborated GTIN indicators, candidate proposals, and batch actions.
 */
export function WorkbenchPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const params = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [brand, setBrand] = useState('');
  const [excludedSubBrands, setExcludedSubBrands] = useState<string[]>([]);
  const [highlight, setHighlight] = useState(0);
  // the active subpage comes from the URL (/workbench, /workbench/rejected…)
  const view: View = viewFromSegment(params.section);

  useEffect(() => {
    if (params.section === 'images') {
      void navigate('/image-studio', { replace: true });
    }
  }, [params.section, navigate]);
  const [bulkProgress, setBulkProgress] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (message === null) return undefined;
    const timer = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [message]);

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [executeTarget, setExecuteTarget] = useState<WcBatch | null>(null);
  // hide view: brand filter feeding the "tout masquer (page)" flow
  const [hideBrand, setHideBrand] = useState<string>('');
  // rejected view: brand filter with numbers, search, sort, pagination, and bulk actions
  const [rejectedBrand, setRejectedBrand] = useState<string>('');
  const [rejectedSearch, setRejectedSearch] = useState<string>('');
  const [rejectedQuery, setRejectedQuery] = useState<string>('');
  const [rejectedSort, setRejectedSort] = useState<'recent' | 'oldest' | 'name' | 'brand' | 'price'>('recent');
  const [rejectedLimit, setRejectedLimit] = useState<number>(300);
  const [selectedRejectedIds, setSelectedRejectedIds] = useState<Set<number>>(new Set());
  const [bulkConfirmModal, setBulkConfirmModal] = useState<{
    action: 'reopen' | 'hide' | 'pack';
    title: string;
    message: string;
    count: number;
    ids: number[];
  } | null>(null);
  const [discontinueTarget, setDiscontinueTarget] = useState<{ id: number; name: string } | null>(
    null,
  );
  const [conflictTarget, setConflictTarget] = useState<{
    productId: number;
    productName: string;
    currentSku: string | null;
    decision: 'approve';
    gtin: string;
    candidateId?: number | null | undefined;
    conflictingProduct: {
      id: number;
      sku: string | null;
      name: string;
    };
    isVariantSibling?: boolean;
  } | null>(null);

  const omnibarInputRef = useRef<HTMLInputElement>(null);
  const [omnibarCode, setOmnibarCode] = useState('');
  const [omnibarScanning, setOmnibarScanning] = useState(false);
  const [variantPicker, setVariantPicker] = useState<{ code: string; items: readonly ScanLookupItem[] } | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
  const [barcodeModalTarget, setBarcodeModalTarget] = useState<{
    productId: number;
    productName: string;
    sku: string | null;
    image: string | null;
    currentBarcode: string | null;
  } | null>(null);
  const [barcodeModalInput, setBarcodeModalInput] = useState('');
  const barcodeModalInputRef = useRef<HTMLInputElement>(null);

  // Competitor search state inside barcode modal (Pharmashop, Parashop, MyCare, Eden Pharma...)
  const [competitorSearchInput, setCompetitorSearchInput] = useState('');
  const [debouncedCompetitorSearch, setDebouncedCompetitorSearch] = useState('');

  useEffect(() => {
    if (barcodeModalTarget !== null) {
      setCompetitorSearchInput(barcodeModalTarget.productName);
      setDebouncedCompetitorSearch(barcodeModalTarget.productName);
    } else {
      setCompetitorSearchInput('');
      setDebouncedCompetitorSearch('');
    }
  }, [barcodeModalTarget]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCompetitorSearch(competitorSearchInput.trim());
    }, 250);
    return () => clearTimeout(timer);
  }, [competitorSearchInput]);

  const competitorSearchQuery = useQuery({
    queryKey: ['competitor-search', debouncedCompetitorSearch],
    queryFn: () =>
      fetchJson<{
        query: string;
        total: number;
        results: Array<{
          id: number;
          sourceKey: string;
          sourceLabel: string;
          sourceTrust: number;
          name: string;
          brand: string | null;
          gtin: string;
          priceTtc: number | null;
          priceHt: number | null;
          url: string | null;
          image: string | null;
          inStock: boolean | null;
          score: number;
        }>;
      }>(`/api/v1/review/competitor-search?q=${encodeURIComponent(debouncedCompetitorSearch)}&limit=15`),
    enabled: barcodeModalTarget !== null && debouncedCompetitorSearch.length >= 2,
  });

  const currentUserQuery = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => fetchJson<{ id: number; email: string; name: string; role: string }>('/api/auth/me'),
  });
  const currentUser = currentUserQuery.data;

  const claimsSummaryQuery = useQuery({
    queryKey: ['review-claims-summary'],
    queryFn: () =>
      fetchJson<{
        totalActiveClaims: number;
        reviewers: readonly {
          user: { id: number; name: string; email: string };
          count: number;
        }[];
      }>('/api/v1/review/claims-summary'),
    enabled: view === 'queue',
    refetchInterval: 15000,
  });

  const releaseClaims = useMutation({
    mutationFn: () =>
      fetchJson<{ ok: boolean; released: number }>('/api/v1/review/release-claims', {
        method: 'POST',
      }),
    onSuccess: (data) => {
      setMessage(`Votre lot (${data.released} produit${data.released > 1 ? 's' : ''}) a été libéré.`);
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['review-claims-summary'] });
    },
    onError: (err) => setMessage('Erreur : ' + String(err.message)),
  });

  const releaseAllClaims = useMutation({
    mutationFn: () =>
      fetchJson<{ ok: boolean; released: number }>('/api/v1/review/admin/release-all-claims', {
        method: 'POST',
      }),
    onSuccess: (data) => {
      setMessage(`Tous les verrous (${data.released} produit${data.released > 1 ? 's' : ''}) ont été libérés.`);
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['review-claims-summary'] });
    },
    onError: (err) => setMessage('Erreur : ' + String(err.message)),
  });

  // bulk hide: mark + push catalog_visibility=hidden to WooCommerce in one
  // verified request (client request 2026-10-07 — hide page / corrections)
  const hideBulkMutation = useMutation({
    mutationFn: (productIds: readonly number[]) =>
      fetchJson<{ ok: boolean; hiddenCount: number; pendingWooPush: number; verified: number; failed: number; batchId: number | null }>(
        '/api/v1/shipping/hide-bulk',
        {
          method: 'POST',
          body: JSON.stringify({ productIds: [...productIds] }),
          signal: AbortSignal.timeout(150_000),
        },
      ),
    onSuccess: (data) => {
      setMessage(
        `${data.hiddenCount} produit(s) masqué(s) · ${data.verified} poussé(s) et relu(s) sur WooCommerce${data.failed > 0 ? `, ${data.failed} échec(s)` : ''} (lot #${data.batchId ?? '—'})`,
      );
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections'] });
      void queryClient.invalidateQueries({ queryKey: ['wc-batches'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
      void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
    },
    onError: (err) => setMessage('Échec du masquage : ' + String(err.message)),
  });

  // bulk unhide: mark + push catalog_visibility=visible to WooCommerce in one
  // verified request (restore products to shop and search results)
  const unhideBulkMutation = useMutation({
    mutationFn: (productIds: readonly number[]) =>
      fetchJson<{ ok: boolean; unhiddenCount: number; pendingWooPush: number; verified: number; failed: number; batchId: number | null }>(
        '/api/v1/shipping/unhide-bulk',
        {
          method: 'POST',
          body: JSON.stringify({ productIds: [...productIds] }),
          signal: AbortSignal.timeout(150_000),
        },
      ),
    onSuccess: (data) => {
      setMessage(
        `${data.unhiddenCount} produit(s) rétabli(s) · ${data.verified} poussé(s) en visibilité visible sur WooCommerce${data.failed > 0 ? `, ${data.failed} échec(s)` : ''} (lot #${data.batchId ?? '—'})`,
      );
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['operator-brands'] });
      void queryClient.invalidateQueries({ queryKey: ['wc-batches'] });
    },
    onError: (err) => setMessage('Échec du rétablissement : ' + String(err.message)),
  });

  // Live counts for all 6 workbench tabs
  const tabCountsQuery = useQuery({
    queryKey: ['workbench-tab-counts'],
    queryFn: () =>
      fetchJson<{
        queue: number;
        corrections: number;
        approved: number;
        rejected: number;
        pack: number;
        hide: number;
        images: number;
      }>('/api/v1/review/tab-counts'),
    refetchInterval: 15_000,
  });

  const queueQuery = useQuery({
    queryKey: ['review-queue', page, query, brand, excludedSubBrands],
    queryFn: () =>
      fetchJson<QueueResponse>(
        `/api/v1/review/queue?page=${page}&limit=25&q=${encodeURIComponent(query)}` +
          (brand !== '' ? `&brand=${encodeURIComponent(brand)}` : '') +
          (excludedSubBrands.length > 0 ? `&excludedSubBrands=${encodeURIComponent(excludedSubBrands.join(','))}` : ''),
      ),
  });

  const brandStatsQuery = useQuery({
    queryKey: ['brand-stats'],
    queryFn: () => fetchJson<readonly BrandStat[]>('/api/v1/review/brand-stats'),
  });

  // the Masqués page and Rejetés have their own brand rosters (operator-brands)
  const decisionsBrand = view === 'hide' ? hideBrand : view === 'rejected' ? rejectedBrand : brand;
  const decisionsSearch = view === 'rejected' ? rejectedQuery : query;
  const decisionsQuery = useQuery({
    queryKey: [
      'decisions',
      view,
      decisionsSearch,
      decisionsBrand,
      view === 'rejected' ? rejectedSort : 'default',
      view === 'rejected' ? rejectedLimit : 300,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('status', view === 'approved' ? 'approved' : view === 'rejected' ? 'rejected' : view);
      if (decisionsSearch.trim() !== '') params.set('q', decisionsSearch.trim());
      if (decisionsBrand.trim() !== '') params.set('brand', decisionsBrand.trim());
      if (view === 'rejected') {
        params.set('sortBy', rejectedSort);
        params.set('limit', String(rejectedLimit));
      }
      return fetchJson<readonly DecisionItem[]>(`/api/v1/review/decisions?${params.toString()}`);
    },
    enabled: view === 'approved' || view === 'rejected' || view === 'pack' || view === 'hide',
  });

  // Deduplicate decisions by productId so each product appears at most once on screen
  const visibleDecisions = useMemo(() => {
    if (!decisionsQuery.data) return [];
    const seen = new Set<number>();
    return decisionsQuery.data.filter((item) => {
      const pid = item.productId ?? item.id;
      if (seen.has(pid)) return false;
      seen.add(pid);
      return true;
    });
  }, [decisionsQuery.data]);

  // brand roster of the currently listed operator status (hide/pack/rejected), with counts
  const operatorBrandsQuery = useQuery({
    queryKey: ['operator-brands', view],
    queryFn: () =>
      fetchJson<{
        readonly brands: readonly { readonly brand: string | null; readonly count: number }[];
        readonly total: number;
        readonly truncated: boolean;
      }>(`/api/v1/review/operator-brands?status=${view === 'rejected' ? 'rejected' : view === 'pack' ? 'pack' : 'hide'}`),
    enabled: view === 'hide' || view === 'pack' || view === 'rejected',
  });

  // pending site-corrections backlog — badge on the 'À corriger (Woo)' tab so
  // approved-but-not-yet-pushed EANs stay visible from every subpage
  const correctionsCountQuery = useQuery({
    queryKey: ['corrections-count'],
    queryFn: () => fetchJson<{ count: number }>('/api/v1/review/corrections-count'),
    refetchInterval: 60_000,
  });

  // operator workflow: pack / hide from any decision page (and the queue)
  const setOperatorStatus = useMutation({
    mutationFn: (input: { productId: number; value: 'pack' | 'hide' | 'none' }) =>
      fetchJson(`/api/v1/products/${input.productId}/operator-status`, {
        method: 'POST',
        body: JSON.stringify({ value: input.value }),
      }),
    onSuccess: (_data, input) => {
      removeProductFromCurrentPage(input.productId);
      setMessage(
        input.value === 'pack'
          ? 'Produit mis de côté (Pack) : hors matching et hors export Sage, reste visible sur le site.'
          : input.value === 'hide'
            ? 'Produit masqué : poussé vers WooCommerce (caché sur le site) via lot vérifié.'
            : 'Produit remis dans le circuit normal.',
      );
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['brand-stats'] });
    },
    onError: (error) => {
      setMessage('Échec : ' + String(error.message));
    },
  });

  // scan-to-approve on the Rejetés subpage: the operator reads the barcode on
  // the physical product; the shared decision endpoint validates mod-10 and
  // raises the 409 transfer dialog when the code belongs to another product
  const [scanValues, setScanValues] = useState<Record<number, string>>({});
  const scanApprove = useMutation({
    mutationFn: (input: { productId: number; productName?: string | undefined; sku?: string | null | undefined; code: string; allowSharedVariant?: boolean | undefined }) => {
      const repaired = repairGtinCheckDigit(input.code) ?? input.code;
      return fetchJson('/api/v1/review/decision', {
        method: 'POST',
        body: JSON.stringify({
          productId: input.productId,
          decision: 'approve',
          gtin: repaired,
          kind: 'validate_current',
          note: 'code scanné sur le produit (page Rejetés)',
          allowSharedVariant: input.allowSharedVariant === true,
          needsWooUpdate: true,
        }),
      });
    },
    onSuccess: (_data, input) => {
      playBeep('success');
      setBarcodeModalTarget(null);
      setBarcodeModalInput('');
      setScanValues((prev) => {
        const next = { ...prev };
        delete next[input.productId];
        return next;
      });
      setMessage('Code validé : produit approuvé, il a rejoint la file « À corriger (Woo) ».');
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['brand-stats'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
      void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
    },
    onError: (error, variables) => {
      if (
        error instanceof ApiError &&
        error.status === 409 &&
        error.data.error === 'gtin_already_assigned' &&
        error.data.conflictingProduct !== undefined
      ) {
        playBeep('warning');
        const repaired = repairGtinCheckDigit(variables.code) ?? variables.code;
        setConflictTarget({
          productId: variables.productId,
          productName: variables.productName ?? `Produit #${variables.productId}`,
          currentSku: variables.sku ?? null,
          decision: 'approve',
          gtin: repaired,
          conflictingProduct: error.data.conflictingProduct,
          isVariantSibling: error.data.isVariantSibling === true,
        });
        return;
      }
      playBeep('error');
      setMessage('Scan refusé : ' + String(error.message));
    },
  });

  const handleOmnibarScan = async (e: React.FormEvent) => {
    e.preventDefault();
    const raw = omnibarCode.trim();
    if (!raw) return;

    setOmnibarScanning(true);
    try {
      const res = await fetchJson<ScanLookupResponse>(`/api/v1/review/scan-lookup?code=${encodeURIComponent(raw)}`);

      if (res.matchType === 'none' || res.items.length === 0) {
        playBeep('error');
        setMessage(`Aucun produit trouvé pour le code [${raw}]. Vous pouvez chercher le produit par SKU ou nom ci-dessous pour lui assigner ce code.`);
        return;
      }

      if (res.matchType === 'single') {
        const target = res.items[0];
        if (!target) return;
        playBeep('success');
        scanApprove.mutate({
          productId: target.id,
          productName: target.name,
          code: res.code,
          allowSharedVariant: true,
        });
        setOmnibarCode('');
        return;
      }

      // Variant family or multiple items
      playBeep('warning');
      setVariantPicker({ code: res.code, items: res.items });
      const rejectedFirst = res.items.find((i) => i.isRejected) ?? res.items[0];
      setSelectedVariantId(rejectedFirst ? rejectedFirst.id : null);
      setOmnibarCode('');
    } catch (err: unknown) {
      playBeep('error');
      setMessage(`Erreur scan : ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setOmnibarScanning(false);
    }
  };

  const handleConfirmVariantSelection = () => {
    if (!variantPicker || selectedVariantId === null) return;
    const selectedItem = variantPicker.items.find((i) => i.id === selectedVariantId);
    scanApprove.mutate({
      productId: selectedVariantId,
      productName: selectedItem?.name,
      code: variantPicker.code,
      allowSharedVariant: true,
    });
    setVariantPicker(null);
    setSelectedVariantId(null);
  };

  const removeProductFromCurrentPage = (productId: number): void => {
    queryClient.setQueryData<QueueResponse>(
      ['review-queue', page, query, brand, excludedSubBrands],
      (old) => {
        if (!old) return old;
        const nextItems = old.items.filter((item) => item.productId !== productId);
        const nextTotal = Math.max(0, old.total - 1);
        const pageSize = old.pageSize || 25;
        return {
          ...old,
          items: nextItems,
          total: nextTotal,
          totalPages: Math.max(1, Math.ceil(nextTotal / pageSize)),
        };
      },
    );
    queryClient.setQueriesData<readonly DecisionItem[]>(
      { queryKey: ['decisions'] },
      (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.filter((item) => (item.productId ?? item.id) !== productId);
      },
    );
    setHighlight((curr) => {
      const currentItems = queueQuery.data?.items ?? [];
      const newLen = Math.max(0, currentItems.length - 1);
      if (newLen <= 0) return 0;
      return Math.min(curr, newLen - 1);
    });
  };

  const discontinue = useMutation({
    mutationFn: (productId: number) =>
      fetchJson('/api/v1/products/' + productId + '/discontinue', {
        method: 'POST',
        body: JSON.stringify({ value: true }),
      }),
    onSuccess: (_data, productId) => {
      setDiscontinueTarget(null);
      setMessage('Produit masqué : hors matching et hors export Sage ; le site le cachera au prochain envoi Woo.');
      removeProductFromCurrentPage(productId);
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
      void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['brand-stats'] });
    },
    onError: (error) => {
      setDiscontinueTarget(null);
      setMessage('Échec : ' + String(error.message));
    },
  });

  const reopen = useMutation({
    mutationFn: (productId: number) =>
      fetchJson('/api/v1/review/reopen', {
        method: 'POST',
        body: JSON.stringify({ productId }),
      }),
    onSuccess: (_data, productId) => {
      removeProductFromCurrentPage(productId);
      setMessage('Produit rétabli : le rejet est annulé, il revient dans la file de revue.');
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['brand-stats'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
    },
    onError: (error) => {
      setMessage('Échec du rétablissement : ' + String(error.message));
    },
  });

  const reopenBulkMutation = useMutation({
    mutationFn: (productIds: readonly number[]) =>
      fetchJson<{ ok: boolean; reopenedCount: number }>('/api/v1/review/reopen-bulk', {
        method: 'POST',
        body: JSON.stringify({ productIds: [...productIds] }),
      }),
    onSuccess: (data) => {
      setMessage(`${data.reopenedCount} produit(s) rétabli(s) : le rejet est annulé, retour dans la file de revue.`);
      setSelectedRejectedIds(new Set());
      setBulkConfirmModal(null);
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['operator-brands'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['brand-stats'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
    },
    onError: (err) => setMessage('Échec du rétablissement groupé : ' + String(err.message)),
  });

  const handleExportRejectedCsv = () => {
    if (visibleDecisions.length === 0) return;
    const header = ['ID', 'SKU', 'Nom', 'Marque', 'Code-barres Woo', 'Date Rejet', 'Auteur Rejet', 'Prix Achat HT', 'Note Rejet'];
    const rows = visibleDecisions.map((d) => [
      d.productId ?? d.id,
      d.product.sku ?? '',
      `"${(d.product.name ?? '').replace(/"/g, '""')}"`,
      `"${(d.product.brand ?? '').replace(/"/g, '""')}"`,
      d.product.barcodeRaw ?? d.product.eanRaw ?? d.gtin ?? '',
      d.decidedAt ? new Date(d.decidedAt).toLocaleString('fr-FR') : '',
      `"${(d.decidedBy ?? '').replace(/"/g, '""')}"`,
      d.product.purchasePriceHt !== null ? String(d.product.purchasePriceHt) : '',
      `"${(d.note ?? '').replace(/"/g, '""')}"`,
    ]);
    const csvContent = '\uFEFF' + [header.join(';'), ...rows.map((r) => r.join(';'))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `produits_rejetes_mapara_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setMessage(`Fichier CSV téléchargé avec succès (${visibleDecisions.length} produits).`);
  };

  // ---- P3 WooCommerce push engine (staging first) ---------------------------
  const executeBatch = useMutation({
    mutationFn: (batchId: number) =>
      fetchJson(`/api/v1/wc-sync/batches/${batchId}/execute`, { method: 'POST' }),
    onSuccess: () => {
      setExecuteTarget(null);
      setMessage('Envoi en cours vers WooCommerce (staging) : suivez l’avancement du lot ci-dessus.');
      void queryClient.invalidateQueries({ queryKey: ['wc-batches'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
    },
    onError: (error) => {
      setExecuteTarget(null);
      setMessage('Échec de l’envoi : ' + String(error.message));
    },
  });

  // DIRECT push (no queue): the API plans, executes and read-back-verifies
  // inside the request; the WAL batch it leaves behind is the audit journal.
  interface PushResult {
    readonly batchId: number;
    readonly itemCount: number;
    readonly noopCount: number;
    readonly sent: number;
    readonly verified: number;
    readonly failed: number;
    readonly errors: readonly string[];
  }
  const pushScope = async (productIds: readonly number[]): Promise<PushResult> =>
    fetchJson<PushResult>('/api/v1/wc-sync/push', {
      method: 'POST',
      body: JSON.stringify({ productIds: [...productIds] }),
      signal: AbortSignal.timeout(150_000),
    });

  const pushErrorText = (error: unknown): string => {
    if (error instanceof ApiError) {
      const base = `${error.data.error ?? 'erreur'}${error.data.detail ? ` — ${error.data.detail}` : ''}`;
      // 404 on a push route almost always means the api service predates it
      const hint = error.status === 404 ? ' (API pas à jour ? redéployez le service api)' : '';
      return `HTTP ${error.status} : ${base}${hint}`;
    }
    return error instanceof Error ? error.message : String(error);
  };

  // Approve + push in ONE operator action (client case 2026-10: Rivaderm — a
  // product with NO site barcode stays without EAN unless the correction is
  // also sent). Records the decision, then pushes the two Woo fields
  // (global_unique_id + REF meta) through the verified WAL pipeline.
  const [pushingIds, setPushingIds] = useState<readonly number[]>([]);
  const approveAndPush = async (input: {
    productId: number;
    productName?: string | undefined;
    sku?: string | null | undefined;
    gtin?: string | null | undefined;
    candidateId?: number | null | undefined;
  }): Promise<void> => {
    setPushingIds((current) => (current.includes(input.productId) ? current : [...current, input.productId]));
    try {
      await decide.mutateAsync({
        productId: input.productId,
        productName: input.productName,
        decision: 'approve',
        gtin: input.gtin,
        candidateId: input.candidateId,
      });
      const r = await pushScope([input.productId]);
      let text: string;
      if (r.failed > 0) {
        text = `Approuvé ; envoi site partiel (lot #${r.batchId}) : ${r.verified} vérifié(s), ${r.failed} échec(s) — ${r.errors.slice(0, 2).join(' · ')}`;
      } else if (r.batchId === 0 || r.sent === 0) {
        text = 'Approuvé — le site affichait déjà ce code.';
      } else {
        text = `Approuvé et poussé au site (lot #${r.batchId}) : ${r.verified} produit relu OK.`;
      }
      setMessage(text);
      void queryClient.invalidateQueries({ queryKey: ['wc-batches'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
    } catch (error) {
      // the 409 conflict dialog is already handled by decide.onError — stay quiet
      if (
        !(
          error instanceof ApiError &&
          error.status === 409 &&
          error.data.error === 'gtin_already_assigned'
        )
      ) {
        const text = `Échec approbation/envoi site : ${pushErrorText(error)}`;
        setMessage(text);
      }
    } finally {
      setPushingIds((current) => current.filter((id) => id !== input.productId));
    }
  };

  const approvePage = useMutation({
    mutationFn: async () => {
      let done = 0;
      const targets = items.filter((item) => item.decided === null && item.candidates.length > 0);
      for (const item of targets) {
        const best = chosenCandidate(item);
        if (best === undefined) continue;
        try {
          await fetchJson('/api/v1/review/decision', {
            method: 'POST',
            body: JSON.stringify({
              productId: item.productId,
              decision: 'approve',
              gtin: best.gtinRaw,
              candidateId: best.candidateId,
            }),
          });
          done += 1;
        } catch (err) {
          if (err instanceof ApiError && err.status === 409) {
            continue;
          }
          throw err;
        }
        setBulkProgress(done + ' / ' + targets.length);
      }
      return done;
    },
    onSuccess: (count) => {
      setBulkProgress(null);
      setApproveOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['brand-stats'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
      setMessage('Page approuvée : ' + count + ' produits (historique dans Approuvés).');
    },
    onError: (error) => {
      setBulkProgress(null);
      setApproveOpen(false);
      setMessage('Échec : ' + String(error.message));
    },
  });

  // bulk-reject: clear a whole page of undecided rows (the none band is mostly
  // weak same-brand near-matches — reject is the honest outcome for those)
  const rejectPage = useMutation({
    mutationFn: async () => {
      let done = 0;
      const targets = items.filter((item) => item.decided === null);
      for (const item of targets) {
        await fetchJson('/api/v1/review/decision', {
          method: 'POST',
          body: JSON.stringify({ productId: item.productId, decision: 'reject' }),
        });
        done += 1;
        setBulkProgress(done + ' / ' + targets.length);
      }
      return done;
    },
    onSuccess: (count) => {
      setBulkProgress(null);
      setRejectOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['brand-stats'] });
      setMessage('Page rejetée : ' + count + ' produits sortis de la file (révisables dans Rejetés).');
    },
    onError: (error) => {
      setBulkProgress(null);
      setRejectOpen(false);
      setMessage('Échec : ' + String(error.message));
    },
  });

  const decide = useMutation({
    mutationFn: (payload: {
      productId: number;
      productName?: string | undefined;
      sku?: string | null | undefined;
      decision: 'approve' | 'reject';
      gtin?: string | null | undefined;
      candidateId?: number | null | undefined;
      force?: boolean | undefined;
      allowSharedVariant?: boolean | undefined;
    }) =>
      fetchJson('/api/v1/review/decision', {
        method: 'POST',
        body: JSON.stringify({
          productId: payload.productId,
          decision: payload.decision,
          gtin: payload.gtin,
          candidateId: payload.candidateId,
          force: payload.force,
          allowSharedVariant: payload.allowSharedVariant,
        }),
      }),
    onSuccess: (_data, variables) => {
      setConflictTarget(null);
      if (variables.force) {
        setMessage('Code réassigné avec succès au produit.');
      } else if (variables.allowSharedVariant) {
        setMessage('Code validé avec succès en tant que variante partagée.');
      } else {
        setMessage('Produit approuvé avec succès.');
      }
      removeProductFromCurrentPage(variables.productId);
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['brand-stats'] });
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
    },
    onError: (error, variables) => {
      if (
        error instanceof ApiError &&
        error.status === 409 &&
        error.data.error === 'gtin_already_assigned' &&
        error.data.conflictingProduct !== undefined &&
        variables.gtin !== undefined &&
        variables.gtin !== null
      ) {
        setConflictTarget({
          productId: variables.productId,
          productName: variables.productName ?? `Produit #${variables.productId}`,
          currentSku: variables.sku ?? null,
          decision: 'approve',
          gtin: variables.gtin,
          candidateId: variables.candidateId,
          conflictingProduct: error.data.conflictingProduct,
          isVariantSibling: error.data.isVariantSibling === true,
        });
        return;
      }
      setMessage('Échec : ' + String(error.message));
    },
  });

  const items = queueQuery.data?.items ?? [];
  const total = queueQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / (queueQuery.data?.pageSize ?? 25)));
  const approvable = items.filter((item) => item.decided === null && item.candidates.length > 0);
  const dialogOpen =
    approveOpen || rejectOpen || discontinueTarget !== null || conflictTarget !== null || executeTarget !== null;

  const emptyQueueHint =
    brand !== ''
      ? `Aucun produit en attente pour la marque « ${brand} ».`
      : 'Tous les produits du catalogue ont été traités.';

  // keyboard flow: j/k navigate, a approve best, r reject — queue view only,
  // so the keys never fire GTIN decisions while another tab is open
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      if (view !== 'queue') return;
      const target = event.target as HTMLElement | null;
      if (target !== null && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (dialogOpen) return;
      if (items.length === 0) return;
      if (event.key === 'j') {
        setHighlight((current) => Math.min(current + 1, items.length - 1));
      } else if (event.key === 'k') {
        setHighlight((current) => Math.max(current - 1, 0));
      } else if (event.key === 'a') {
        const item = items[highlight];
        const best = item !== undefined ? chosenCandidate(item) : undefined;
        const ourGtin = item ? (item.product.barcodeRaw ?? item.product.eanRaw) : null;
        const ourEffective = effectiveGtin(ourGtin);
        const codeToApprove = best?.gtinRaw
          ? (repairGtinCheckDigit(best.gtinRaw) ?? best.gtinRaw)
          : (ourEffective ?? undefined);
        if (item !== undefined && codeToApprove !== undefined && item.decided === null) {
          decide.mutate({
            productId: item.productId,
            productName: item.product.name,
            sku: item.product.sku,
            decision: 'approve',
            gtin: codeToApprove,
            candidateId: best?.candidateId ?? null,
          });
        }
      } else if (event.key === 'r') {
        const item = items[highlight];
        if (item !== undefined && item.decided === null) {
          decide.mutate({ productId: item.productId, decision: 'reject' });
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [items, highlight, decide, dialogOpen, view]);

  useEffect(() => {
    if (barcodeModalTarget !== null) {
      const timer = setTimeout(() => {
        barcodeModalInputRef.current?.focus();
        barcodeModalInputRef.current?.select();
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [barcodeModalTarget]);

  return (
    <div>
      <PageHeader
        index="S02"
        title={view === 'images' ? 'Image Studio' : 'GTIN Workbench'}
        purpose={
          view === 'images'
            ? 'De belles photos pour chaque produit : comparez la photo actuelle du site avec les photos officielles de la marque, remettez-les dans l’ordre, puis envoyez. Les anciennes photos restent sauvegardées.'
            : 'Poste de décision barcode : pour chaque produit, comparez notre code au meilleur candidat trouvé chez les sources, approuvez le bon GTIN ou rejetez la file. Rien ne part sur le site sans une décision tracée.'
        }
        meta={
          view === 'corrections'
            ? [
                {
                  label: 'File',
                  value: `${(tabCountsQuery.data?.corrections ?? correctionsCountQuery.data?.count ?? 0).toLocaleString('fr-FR')} à corriger`,
                },
              ]
            : view === 'queue'
              ? [
                  { label: 'File', value: `${(tabCountsQuery.data?.queue ?? total).toLocaleString('fr-FR')} produits` },
                  { label: 'Page', value: `${page} / ${totalPages}` },
                  { label: 'Sur cette page', value: `${items.length} restant${items.length > 1 ? 's' : ''}` },
                ]
              : view === 'images'
                ? []
                : [
                    {
                      label: 'Total',
                      value: `${(tabCountsQuery.data?.[view] ?? visibleDecisions.length).toLocaleString('fr-FR')} produits`,
                    },
                    { label: 'Affichés', value: `${visibleDecisions.length} produits` },
                  ]
        }
        actions={
          <Segmented<View>
            ariaLabel="Vue du workbench"
            value={view}
            onChange={(next) => {
              setPage(1);
              setHighlight(0);
              // The hidden products live on their own screen (S29).
              if (next === 'hide') {
                void navigate('/hidden-products');
                return;
              }
              // Image Studio lives on its own dedicated screen (S33).
              if (next === 'images') {
                void navigate('/image-studio');
                return;
              }
              const segment = VIEW_SEGMENTS[next];
              void navigate(segment === '' ? '/workbench' : `/workbench/${segment}`);
            }}
            options={[
              {
                value: 'queue',
                label: `File d'attente${tabCountsQuery.data !== undefined ? ` (${tabCountsQuery.data.queue.toLocaleString('fr-FR')})` : ''}`,
              },
              {
                value: 'corrections',
                label: `À corriger (Woo)${tabCountsQuery.data !== undefined ? ` (${tabCountsQuery.data.corrections.toLocaleString('fr-FR')})` : (correctionsCountQuery.data?.count ?? 0) > 0 ? ` (${correctionsCountQuery.data?.count})` : ''}`,
              },
              {
                value: 'approved',
                label: `Approuvés${tabCountsQuery.data !== undefined ? ` (${tabCountsQuery.data.approved.toLocaleString('fr-FR')})` : ''}`,
              },
              {
                value: 'rejected',
                label: `Rejetés${tabCountsQuery.data !== undefined ? ` (${tabCountsQuery.data.rejected.toLocaleString('fr-FR')})` : ''}`,
              },
              {
                value: 'pack',
                label: `Pack${tabCountsQuery.data !== undefined ? ` (${tabCountsQuery.data.pack.toLocaleString('fr-FR')})` : ''}`,
              },
              {
                value: 'hide',
                label: `Masqués${tabCountsQuery.data !== undefined ? ` (${tabCountsQuery.data.hide.toLocaleString('fr-FR')})` : ''}`,
              },
              { value: 'images', label: 'Image Studio' },
            ]}
          />
        }
      />

      <Panel className="mb-4">
        <PanelBody className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            {view === 'queue' && (
              <>
                <form
                  className="flex items-center gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    setPage(1);
                    setQuery(search.trim());
                  }}
                >
                  <div className="relative">
                    <input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="SKU, nom, marque…"
                      aria-label="Rechercher dans la file"
                      className="h-8.5 w-52 sm:w-64 rounded-lg border border-border bg-card pl-8 pr-3 text-xs sm:text-sm text-ink placeholder:text-ink-ghost focus:border-herb-600 focus:ring-2 focus:ring-herb-500/20 outline-none transition-all shadow-xs"
                    />
                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-ink-ghost pointer-events-none" />
                  </div>
                  <Button type="submit" variant="primary" size="sm">
                    Chercher
                  </Button>
                  {query !== '' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSearch('');
                        setQuery('');
                        setPage(1);
                      }}
                    >
                      Effacer
                    </Button>
                  )}
                </form>
                <div className="w-60 sm:w-72">
                  <Select
                    aria-label="Filtrer par marque"
                    value={brand}
                    onChange={(event) => {
                      const nextBrand = event.target.value;
                      setBrand(nextBrand);
                      setExcludedSubBrands([]);
                      setPage(1);
                      setHighlight(0);
                    }}
                    className="h-8.5 py-1 text-xs sm:text-sm"
                  >
                    <option value="">Toutes les marques</option>
                    {brandStatsQuery.data?.map((stat) => (
                      <option key={stat.key} value={stat.key}>
                        {`${stat.brand} — ${stat.pending} à traiter / ${stat.done} traités`}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="w-full">
                  <SubBrandPills
                    brand={brand}
                    excludedSubBrands={excludedSubBrands}
                    onChange={(ex) => {
                      setExcludedSubBrands(ex);
                      setPage(1);
                    }}
                  />
                </div>
              </>
            )}
            {view === 'rejected' && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-semibold text-ink-soft">
                  Rejetés : <strong>{(operatorBrandsQuery.data?.total ?? tabCountsQuery.data?.rejected ?? visibleDecisions.length).toLocaleString('fr-FR')}</strong> produits
                </span>
                {rejectedBrand !== '' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-herb-100 text-herb-900 px-2.5 py-0.5 text-xs font-bold border border-herb-300">
                    Filtre : {rejectedBrand === '__NOBRAND__' ? 'Sans marque' : rejectedBrand}
                    <button type="button" onClick={() => setRejectedBrand('')} className="hover:text-rose-700 ml-1 font-bold">×</button>
                  </span>
                )}
                {rejectedQuery !== '' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-900 px-2.5 py-0.5 text-xs font-bold border border-sky-300">
                    Recherche : &laquo;{rejectedQuery}&raquo;
                    <button type="button" onClick={() => { setRejectedSearch(''); setRejectedQuery(''); }} className="hover:text-rose-700 ml-1 font-bold">×</button>
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {view === 'queue' && (
              <span className="hidden items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-ink-faint xl:flex">
                <Key>j</Key>
                <Key>k</Key> naviguer
                <Key>a</Key> approuver
                <Key>r</Key> rejeter
              </span>
            )}

            {view === 'queue' && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={releaseClaims.isPending || (queueQuery.data?.activeClaimsCount ?? 0) === 0}
                  onClick={() => releaseClaims.mutate()}
                  icon={<Unlock className="h-3.5 w-3.5" />}
                  title="Libérer les produits réservés pour ce lot pour qu'un autre relecteur puisse les traiter"
                >
                  {releaseClaims.isPending ? 'Libération…' : 'Libérer mon lot'}
                </Button>
                {currentUser?.role === 'admin' && (claimsSummaryQuery.data?.totalActiveClaims ?? 0) > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={releaseAllClaims.isPending}
                    onClick={() => releaseAllClaims.mutate()}
                    icon={<Unlock className="h-3.5 w-3.5" />}
                    title="Libérer tous les verrous actifs sur les relecteurs (Admin)"
                  >
                    {releaseAllClaims.isPending ? 'Libération…' : 'Libérer tous les verrous'}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={rejectPage.isPending || items.every((item) => item.decided !== null)}
                  onClick={() => setRejectOpen(true)}
                >
                  {rejectPage.isPending
                    ? `Rejet… ${bulkProgress ?? ''}`
                    : `Rejeter la page (${items.filter((item) => item.decided === null).length})`}
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={approvePage.isPending || approvable.length === 0}
                  onClick={() => setApproveOpen(true)}
                  icon={<CheckCheck className="h-4 w-4" />}
                >
                  {approvePage.isPending
                    ? `Approbation… ${bulkProgress ?? ''}`
                    : `Approuver la page (${approvable.length})`}
                </Button>
              </>
            )}
          </div>
        </PanelBody>
        {approvePage.isPending && (
          <PanelBody className="border-t border-border pt-3">
            <ProgressBar value={null} label="Approbation en cours — ne fermez pas l’onglet" />
          </PanelBody>
        )}
      </Panel>

      {message !== null && (
        <div className="fixed bottom-5 right-5 z-50 max-w-md animate-rise shadow-pop">
          <Alert
            tone={message.startsWith('Échec') ? 'alarm' : 'good'}
            title={message.startsWith('Échec') ? 'Erreur' : 'Notification'}
            actions={
              <button
                type="button"
                onClick={() => setMessage(null)}
                className="text-xs font-semibold opacity-70 hover:opacity-100 p-1 cursor-pointer"
                aria-label="Fermer"
              >
                ✕
              </button>
            }
          >
            {message}
          </Alert>
        </div>
      )}

      {view === 'queue' && (
        <>
          {/* Multi-reviewer workload & exclusive reservation banner */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card/80 px-4 py-3 text-xs shadow-xs">
            <div className="flex items-center gap-2.5">
              <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-emerald-200 animate-pulse" />
              <div>
                <span className="font-semibold text-ink">Mode Multi-Relecteur Actif :</span>{' '}
                <span className="text-ink-soft">
                  {queueQuery.data?.activeClaimsCount
                    ? `Lot exclusif de ${queueQuery.data.activeClaimsCount} produit${queueQuery.data.activeClaimsCount > 1 ? 's' : ''} réservé pour vous (aucun autre relecteur ne voit ces produits).`
                    : 'File prête sans réservation active.'}
                </span>
              </div>
            </div>
            {claimsSummaryQuery.data && claimsSummaryQuery.data.reviewers.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-ink-faint">Relecteurs au travail :</span>
                {claimsSummaryQuery.data.reviewers.map((r) => (
                  <span
                    key={r.user.id}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors',
                      r.user.id === currentUser?.id
                        ? 'bg-herb-50 text-herb-700 border border-herb-200'
                        : 'bg-muted text-ink-muted border border-border',
                    )}
                  >
                    <span>👤 {r.user.name}</span>
                    <span className="font-mono text-[10px] opacity-75">({r.count} en cours)</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          {queueQuery.isError && (
            <Alert tone="alarm" title="File indisponible" className="mb-4">
              {String(queueQuery.error.message)}
            </Alert>
          )}
          {queueQuery.isPending && <Loading label="Lecture de la file de revue…" />}
          {queueQuery.data !== undefined && items.length === 0 && total === 0 && (
            <div className="py-4">
              <EmptyState
                title="File de revue vide"
                hint={emptyQueueHint}
              />
            </div>
          )}

          {queueQuery.data !== undefined && items.length === 0 && total > 0 && (
            <div className="rounded-xl border border-herb-600/30 bg-herb-50/20 p-8 text-center shadow-xs">
              <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-herb-100 text-herb-700 mb-3">
                <CheckCheck className="h-6 w-6" />
              </div>
              <h3 className="text-base font-bold text-ink">Page terminée !</h3>
              <p className="mt-1 text-sm text-ink-soft">
                Tous les produits de cette page ont été traités. Il reste encore{' '}
                <span className="font-semibold text-ink">
                  {total.toLocaleString('fr-FR')} produit{total > 1 ? 's' : ''}
                </span>{' '}
                à traiter dans cette file.
              </p>
              <div className="mt-4 flex justify-center gap-3">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setHighlight(0);
                    setPage((p) => Math.min(p, totalPages));
                    void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
                  }}
                  icon={<ChevronRight className="h-4 w-4" />}
                >
                  Charger les 25 produits suivants
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-2.5">
            {items.map((item, index) => {
              const best = chosenCandidate(item);
              const bestRaw = best?.gtinRaw;
              const bestRepaired = bestRaw ? repairGtinCheckDigit(bestRaw) : null;
              const bestIsRepaired = bestRaw !== null && bestRepaired !== null && bestRaw !== bestRepaired;
              const bestEffectiveCode = bestIsRepaired ? bestRepaired : bestRaw;
              const ourImage = item.product.images[0];
              const ourGtin = item.product.barcodeRaw ?? item.product.eanRaw;
              const ourEffective = effectiveGtin(ourGtin);
              // contenance cross-check (client request): our pack vs the best candidate's
              const ourCont = contenanceOf(item.product.sizeValue, item.product.sizeUnit);
              const bestCont = contenanceOf(best?.sizeValue ?? null, best?.sizeUnit ?? null);
              const contMatch: boolean | null =
                ourCont !== null && bestCont !== null ? sameContenance(ourCont, bestCont) : null;
              const isHighlighted = index === highlight;
              const decided = item.decided !== null;
              // corroborated = ANY candidate carries our current code (including repaired check digit)
              const matchesBest =
                ourGtin !== undefined &&
                ourGtin !== '' &&
                item.candidates.some((c) => {
                  if (c.gtinRaw === null) return false;
                  const cEff = effectiveGtin(c.gtinRaw);
                  return (
                    digitsOf(c.gtinRaw) === digitsOf(ourGtin) ||
                    (cEff !== null && ourEffective !== null && cEff === ourEffective)
                  );
                });

              return (
                <article
                  key={item.productId}
                  onClick={() => setHighlight(index)}
                  className={cn(
                    'relative overflow-hidden rounded-xl border bg-card shadow-xs transition-all duration-150',
                    isHighlighted
                      ? 'border-herb-600/40 ring-1 ring-herb-500/20 bg-herb-50/15'
                      : 'border-border hover:border-border-strong',
                    decided && 'opacity-65',
                  )}
                >
                  <div className="relative flex flex-wrap items-start gap-3.5 p-3.5">
                    {/* Product Thumbnail */}
                    {ourImage !== undefined && ourImage !== '' ? (
                      <img
                        src={ourImage}
                        alt=""
                        className="h-20 w-20 sm:h-24 sm:w-24 shrink-0 rounded-lg border border-border object-cover bg-cream"
                      />
                    ) : (
                      <div className="grid h-20 w-20 sm:h-24 sm:w-24 shrink-0 place-items-center rounded-lg border border-dashed border-border-strong bg-cream text-ink-ghost">
                        <ImageIcon className="h-7 w-7 opacity-60" />
                      </div>
                    )}

                    {/* Product Info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-mono text-xs font-bold text-ink bg-cream px-1.5 py-0.5 rounded border border-border">
                          {item.product.sku ?? '∅'}
                        </span>
                        <Badge tone={item.band === 'auto' ? 'good' : 'signal'}>
                          score {score(item.bestScore)}
                        </Badge>
                        <Badge tone="muted">
                          {item.candidates.length} candidat{item.candidates.length > 1 ? 's' : ''}
                        </Badge>
                        {item.decided !== null && <Badge tone="scan">{item.decided}</Badge>}
                        {item.claim && !item.claim.isMine && (
                          <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 border border-amber-200">
                            🔒 En cours par {item.claim.userName}
                          </span>
                        )}
                        {item.claim && item.claim.isMine && (
                          <span className="inline-flex items-center gap-1 rounded bg-herb-50 px-2 py-0.5 text-[11px] font-medium text-herb-700 border border-herb-200">
                            🎯 Votre lot
                          </span>
                        )}
                      </div>

                      <h3 className="mt-1 text-base sm:text-[17px] font-bold text-ink leading-snug tracking-tight">
                        {toCapitalizedCase(item.product.name)}
                      </h3>

                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-soft">
                        {/* Corroborated GTIN Display */}
                        <div className="inline-flex items-center gap-1.5">
                          <span className="font-mono text-[11px] font-semibold uppercase tracking-wider text-ink-faint">
                            GTIN actuel:
                          </span>
                          <span
                            className={cn(
                              'font-mono tabular text-xs sm:text-[13px] font-bold px-2 py-0.5 rounded border',
                              matchesBest
                                ? 'bg-herb-50 border-herb-600/30 text-herb-700'
                                : ourGtin === undefined || ourGtin === ''
                                  ? 'bg-cream border-border text-ink-ghost'
                                  : 'bg-rust-50 border-rust-600/30 text-rust-700',
                            )}
                          >
                            {ourGtin ?? 'AUCUN'}
                          </span>
                          <span className="text-[11px] font-semibold uppercase tracking-wider">
                            {matchesBest ? (
                              <span className="text-herb-700 font-bold">✓ Corroboré</span>
                            ) : ourGtin ? (
                              <span className="text-rust-600 font-bold">≠ Diffère</span>
                            ) : null}
                          </span>
                        </div>

                        <span>{item.product.brand ?? '—'}</span>
                        <span className="tabular font-mono font-medium text-ink">
                          {money(item.product.regularPriceTtc)} TND TTC
                        </span>
                        <span className="font-mono text-[10px] text-ink-ghost">
                          wc #{item.product.wcProductId}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        disabled={decide.isPending || decided || (best === undefined && !ourEffective)}
                        onClick={() => {
                          const codeToApprove = bestEffectiveCode || ourEffective;
                          if (!codeToApprove) return;
                          decide.mutate({
                            productId: item.productId,
                            productName: item.product.name,
                              sku: item.product.sku,
                            decision: 'approve',
                            gtin: codeToApprove,
                            candidateId: best?.candidateId ?? null,
                          });
                        }}
                        icon={<Check className="h-4 w-4" />}
                        className="font-semibold shadow-xs"
                      >
                        Approuver
                      </Button>
                      <Button
                        type="button"
                        variant="soft"
                        size="sm"
                        disabled={
                          decided ||
                          best === undefined ||
                          pushingIds.includes(item.productId) ||
                          decide.isPending
                        }
                        onClick={() => {
                          if (best === undefined) return;
                          void approveAndPush({
                            productId: item.productId,
                            productName: item.product.name,
                              sku: item.product.sku,
                            gtin: bestEffectiveCode,
                            candidateId: best.candidateId,
                          });
                        }}
                        icon={<CloudUpload className="h-4 w-4" />}
                        title="Approuve ce code ici PUIS l'écrit sur le site Woo (global_unique_id + REF plugin, relecture incluse) — indispensable quand le site n'a pas encore de code-barres"
                      >
                        {pushingIds.includes(item.productId) ? 'Envoi site…' : 'Approuver + site'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={decide.isPending || decided}
                        onClick={() =>
                          decide.mutate({ productId: item.productId, decision: 'reject' })
                        }
                        icon={<X className="h-4 w-4" />}
                      >
                        Rejeter
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={setOperatorStatus.isPending}
                        title="Mettre de côté : hors matching et hors export Sage, reste visible sur le site"
                        onClick={() =>
                          setOperatorStatus.mutate({ productId: item.productId, value: 'pack' })
                        }
                        icon={<Package className="h-4 w-4" />}
                      >
                        Pack
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        disabled={setOperatorStatus.isPending}
                        title="Masquer du site au prochain envoi Woo + hors export Sage"
                        onClick={() =>
                          setOperatorStatus.mutate({ productId: item.productId, value: 'hide' })
                        }
                        icon={<EyeOff className="h-4 w-4" />}
                      >
                        Masquer
                      </Button>

                      {/* Quick scan / manual barcode input */}
                      <form
                        className="flex items-center gap-1.5"
                        onSubmit={(e) => {
                          e.preventDefault();
                          const val = (scanValues[item.productId] ?? '').trim();
                          if (!val) return;
                          const repaired = repairGtinCheckDigit(val) ?? val;
                          decide.mutate({
                            productId: item.productId,
                            productName: item.product.name,
                              sku: item.product.sku,
                            decision: 'approve',
                            gtin: repaired,
                          });
                        }}
                      >
                        <div className="relative">
                          <input
                            value={scanValues[item.productId] ?? ''}
                            onChange={(e) =>
                              setScanValues((prev) => ({ ...prev, [item.productId]: e.target.value }))
                            }
                            placeholder="Scanner GTIN…"
                            aria-label={`Code-barres pour ${item.product.name}`}
                            className="h-8 w-36 sm:w-44 rounded-lg border border-border bg-card pl-7 pr-2 font-mono text-xs text-ink placeholder:text-ink-ghost focus:border-herb-600 focus:ring-2 focus:ring-herb-500/20 outline-none"
                          />
                          <ScanLine className="absolute left-2 top-2 h-4 w-4 text-ink-ghost pointer-events-none" />
                        </div>
                        <Button
                          type="submit"
                          variant="soft"
                          size="sm"
                          disabled={decide.isPending || decided || !(scanValues[item.productId] ?? '').trim()}
                        >
                          Valider
                        </Button>
                      </form>
                    </div>
                  </div>

                  {/* Best Candidate Proposal Highlight Strip */}
                  {best !== undefined && (
                    <div
                      className={cn(
                        'mx-3.5 mb-3 flex flex-col gap-2.5 rounded-xl border p-3 sm:p-3.5 transition-all shadow-xs',
                        matchesBest
                          ? 'border-herb-600/30 bg-herb-50/20'
                          : 'border-amber-500/30 bg-amber-50/20',
                      )}
                    >
                      {/* Top Row: Context & Metadata */}
                      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 text-xs">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
                          <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-ink bg-card px-2 py-0.5 rounded-md border border-border shadow-2xs">
                            Meilleur candidat
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <Barcode
                              value={bestEffectiveCode}
                              tone={matchesBest ? 'good' : 'alarm'}
                              height={16}
                            />
                            {bestIsRepaired ? (
                              <span
                                className="tabular font-mono text-xs sm:text-[13px] font-bold px-2 py-0.5 rounded-md border bg-amber-50 border-amber-300 text-amber-900 inline-flex items-center gap-1"
                                title={`Code source brut: ${best.gtinRaw} (clé mod-10 corrigée: ${bestEffectiveCode})`}
                              >
                                <span className="line-through opacity-60">{best.gtinRaw}</span>
                                <span>{bestEffectiveCode}</span>
                                <span className="text-[10px] uppercase tracking-wider bg-amber-200/70 px-1 rounded font-sans">clé corrigée</span>
                              </span>
                            ) : (
                              <span
                                className={cn(
                                  'tabular font-mono text-xs sm:text-[13px] font-bold px-2 py-0.5 rounded-md border',
                                  matchesBest
                                    ? 'bg-herb-50 border-herb-600/40 text-herb-700'
                                    : 'bg-rust-50 border-rust-600/40 text-rust-700',
                                )}
                              >
                                {best.gtinRaw ?? '∅'}
                              </span>
                            )}
                            <span className="text-[11px] font-bold uppercase tracking-wider">
                              {matchesBest ? (
                                <span className="text-herb-700">✓ Même code</span>
                              ) : (
                                <span className="text-rust-600">≠ Code différent</span>
                              )}
                            </span>
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-2.5 font-mono text-xs text-ink-soft">
                          <span>
                            <span className="font-bold text-ink">{cleanSourceLabel(best.sourceLabel)}</span>{' '}
                            <span className="text-ink-faint">(confiance {best.sourceTrust})</span>
                          </span>
                          <span className="text-border-strong">·</span>
                          <span>
                            score <span className="font-bold text-herb-700">{score(best.scoreTotal)}</span>
                          </span>
                          {best.priceTtc !== null && (
                            <>
                              <span className="text-border-strong">·</span>
                              <span className="tabular text-ink font-semibold">TTC {money(best.priceTtc)}</span>
                            </>
                          )}
                          {best.url !== null && (
                            <>
                              <span className="text-border-strong">·</span>
                              <a
                                href={best.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sky-700 inline-flex items-center gap-1 hover:text-sky-600 hover:underline font-sans font-medium"
                              >
                                source <ExternalLink className="h-3 w-3" />
                              </a>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Prominent Candidate Name Row */}
                      <div className="pt-2 border-t border-border/60 flex items-start gap-3">
                        {best.image !== null && best.image !== '' ? (
                          <img
                            src={best.image}
                            alt=""
                            className="h-20 w-20 sm:h-24 sm:w-24 shrink-0 rounded-lg border border-border object-cover bg-card shadow-2xs"
                          />
                        ) : (
                          <div className="grid h-20 w-20 sm:h-24 sm:w-24 shrink-0 place-items-center rounded-lg border border-dashed border-border bg-card text-ink-ghost">
                            <ImageIcon className="h-7 w-7 opacity-40" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <h4 className="text-base sm:text-[17px] font-bold text-ink leading-snug tracking-tight break-words">
                            <HighlightedMatch target={best.nameRaw} reference={item.product.name} />
                          </h4>
                          {contMatch !== null && ourCont !== null && bestCont !== null && (
                            <span
                              className={cn(
                                'mt-1 inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold',
                                contMatch
                                  ? 'border-herb-600/30 bg-herb-50 text-herb-700'
                                  : 'border-rust-600/40 bg-rust-50 text-rust-700',
                              )}
                              title={
                                contMatch
                                  ? 'Contenance identique des deux côtés'
                                  : 'Attention : le candidat ne correspond pas à la contenance de notre produit'
                              }
                            >
                              {contMatch ? '✓' : '⚠'} Contenance :{' '}
                              {formatContenance(ourCont)}
                              {contMatch ? ' = ' : ' ≠ '}
                              {formatContenance(bestCont)}
                            </span>
                          )}
                        </div>
                        {best.brandRaw && (
                          <span className="shrink-0 font-mono text-xs font-semibold text-ink-soft bg-card px-2 py-0.5 rounded-md border border-border shadow-2xs">
                            {best.brandRaw}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Source Evidence List - Always Open By Default */}
                  <div className="border-t border-border bg-card/60 px-3.5 py-2.5">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <SectionLabel>Preuves sources ({item.candidates.length})</SectionLabel>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                        {matchesBest
                          ? '✓ notre code est corroboré'
                          : '≠ notre code diffère du meilleur candidat'}
                      </span>
                    </div>

                    <ul className="space-y-1.5">
                      {[...item.candidates]
                        .sort((a, b) => {
                          const pA = candidatePriority(a);
                          const pB = candidatePriority(b);
                          if (pA !== pB) return pA - pB;
                          return Number(b.scoreTotal) - Number(a.scoreTotal);
                        })
                        .map((candidate) => {
                        const rawCode = candidate.gtinRaw;
                        const repairedCode = rawCode ? repairGtinCheckDigit(rawCode) : null;
                        const isCheckDigitFixed = rawCode !== null && repairedCode !== null && rawCode !== repairedCode;
                        const effectiveCode = isCheckDigitFixed ? repairedCode : rawCode;
                        const same =
                          ourGtin !== undefined &&
                          ourGtin !== '' &&
                          (effectiveCode === ourGtin || (effectiveCode !== null && ourEffective !== null && effectiveCode === ourEffective));
                        return (
                          <li
                            key={candidate.candidateId}
                            className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg px-3 py-2 text-xs transition-colors odd:bg-cream/40 even:bg-card hover:bg-cream/70 border border-transparent hover:border-border"
                          >
                            <Barcode
                              value={effectiveCode}
                              tone={same ? 'good' : 'alarm'}
                              height={16}
                            />
                            {isCheckDigitFixed ? (
                              <span
                                className="tabular font-mono text-xs sm:text-[13px] font-bold px-2 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-300 inline-flex items-center gap-1"
                                title={`Code source brut: ${rawCode} (clé mod-10 corrigée: ${repairedCode})`}
                              >
                                <span className="line-through opacity-60">{rawCode}</span>
                                <span>{repairedCode}</span>
                                <span className="text-[10px] uppercase tracking-wider bg-amber-200/70 px-1 rounded font-sans">clé corrigée</span>
                              </span>
                            ) : (
                              <span
                                className={cn(
                                  'tabular font-mono text-xs sm:text-[13px] font-bold px-2 py-0.5 rounded',
                                  same ? 'bg-herb-50 text-herb-700' : 'bg-rust-50 text-rust-700',
                                )}
                              >
                                {candidate.gtinRaw ?? '∅'}
                              </span>
                            )}
                            <span className="min-w-0 flex-1 text-sm font-semibold text-ink leading-snug break-words">
                              <HighlightedMatch target={candidate.nameRaw} reference={item.product.name} />
                            </span>
                            <span className="hidden font-mono text-xs text-ink-soft sm:inline">
                              {candidate.brandRaw ?? '—'}
                            </span>
                            <span className="tabular font-mono text-xs text-ink-soft">
                              {candidate.priceHt === null
                                ? ''
                                : COMPETITOR_SOURCE_KEYS.has(candidate.sourceKey)
                                  ? `HT ${money(candidate.priceHt)} · `
                                  : `achat ${money(candidate.priceHt)} HT · `}
                              {candidate.priceTtc === null
                                ? ''
                                : `TTC ${money(candidate.priceTtc)} · `}
                              <span className="font-semibold text-ink">{cleanSourceLabel(candidate.sourceLabel)}</span> ({candidate.sourceTrust})
                            </span>
                            <span className="tabular font-mono text-xs font-bold text-clay-600">
                              {score(candidate.scoreTotal)}
                            </span>
                            {candidate.url !== null && (
                              <a
                                href={candidate.url}
                                target="_blank"
                                rel="noreferrer"
                                title="Ouvrir la page source archivée"
                                className="text-sky-700 hover:text-sky-600 p-0.5"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                            <Button
                              type="button"
                              variant="soft"
                              size="xs"
                              disabled={decide.isPending || effectiveCode === null || decided}
                              onClick={() =>
                                decide.mutate({
                                  productId: item.productId,
                                  productName: item.product.name,
                              sku: item.product.sku,
                                  decision: 'approve',
                                  gtin: effectiveCode,
                                  candidateId: candidate.candidateId,
                                })
                              }
                            >
                              Approuver ce code
                            </Button>
                          </li>
                        );
                      })}
                      {item.candidates.length === 0 && (
                        <li className="text-xs text-ink-faint py-1">
                          Aucune preuve source pour ce produit — rejetez la ligne ou importez une liste fournisseur (S06).
                        </li>
                      )}
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Bottom Pagination & Fast Action Toolbar */}
          {total > 0 && (
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => {
                    setPage((current) => current - 1);
                    setHighlight(0);
                  }}
                  icon={<ChevronLeft className="h-3.5 w-3.5" />}
                >
                  Précédent
                </Button>
                <span className="tabular font-mono text-xs font-medium text-ink-soft px-2">
                  Page {page} / {totalPages} · {items.length} restant{items.length > 1 ? 's' : ''} sur cette page ({total.toLocaleString('fr-FR')} au total)
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => {
                    setPage((current) => current + 1);
                    setHighlight(0);
                  }}
                >
                  Suivant <ChevronRight className="h-3.5 w-3.5 inline ml-1" />
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={queueQuery.isFetching}
                  onClick={() => void queueQuery.refetch()}
                  icon={<RotateCw className={cn('h-3.5 w-3.5', queueQuery.isFetching && 'animate-spin')} />}
                >
                  {queueQuery.isFetching ? 'Lecture…' : 'Recharger'}
                </Button>

                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={approvePage.isPending || approvable.length === 0}
                  onClick={() => setApproveOpen(true)}
                  icon={<CheckCheck className="h-4 w-4" />}
                >
                  {approvePage.isPending
                    ? `Approbation… ${bulkProgress ?? ''}`
                    : `Approuver la page (${approvable.length})`}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {view === 'corrections' && <WorkbenchCorrectionsPage />}

      {(view === 'approved' || view === 'rejected' || view === 'pack' || view === 'hide') && (
        <>
          {view === 'rejected' && (
            <div className="mb-4 rounded-xl border border-herb-600/30 bg-herb-50/40 p-4 shadow-xs">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-herb-600 text-white shadow-xs">
                    <ScanLine className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-ink">Vérification physique par scan EAN (douchette / pistolet)</h3>
                    <p className="text-xs text-ink-soft">
                      Scannez l&apos;emballage physique. En cas de variantes multiples (ex: GUM Verte/Jaune/Bleue), vous pourrez sélectionner la variante en main.
                    </p>
                  </div>
                </div>
              </div>
              <form onSubmit={handleOmnibarScan} className="mt-3 flex items-center gap-2">
                <div className="relative flex-1">
                  <input
                    ref={omnibarInputRef}
                    type="text"
                    value={omnibarCode}
                    onChange={(e) => setOmnibarCode(e.target.value)}
                    placeholder="Scanner le code-barres physique (EAN-13)..."
                    aria-label="Code-barres physique"
                    autoFocus
                    className="h-10 w-full rounded-lg border border-border bg-card pl-10 pr-3 font-mono text-sm text-ink placeholder:text-ink-ghost focus:border-herb-600 focus:ring-2 focus:ring-herb-500/20 outline-none"
                  />
                  <ScanLine className="absolute left-3 top-2.5 h-5 w-5 text-ink-ghost pointer-events-none" />
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={omnibarScanning || omnibarCode.trim() === ''}
                  icon={<Check className="h-4 w-4" />}
                >
                  {omnibarScanning ? 'Recherche…' : 'Vérifier'}
                </Button>
              </form>
            </div>
          )}

          {decisionsQuery.isError && (
            <Alert
              tone="alarm"
              title={view === 'hide' ? 'Produits masqués indisponibles' : view === 'pack' ? 'Produits pack indisponibles' : 'Décisions indisponibles'}
              className="mb-3"
            >
              {String(decisionsQuery.error.message)}
            </Alert>
          )}
          {decisionsQuery.isPending && (
            <Loading
              label={
                view === 'hide'
                  ? 'Lecture des produits masqués…'
                  : view === 'pack'
                    ? 'Lecture des produits pack…'
                    : "Lecture de l'historique des décisions…"
              }
            />
          )}
          {decisionsQuery.data !== undefined && visibleDecisions.length === 0 && (
            <EmptyState
              title={
                view === 'approved'
                  ? 'Aucune décision approuvée'
                  : view === 'hide'
                    ? 'Aucun produit masqué'
                    : view === 'pack'
                      ? 'Aucun produit en Pack'
                      : 'Aucune décision rejetée'
              }
              hint={
                view === 'hide'
                  ? "Les produits « Masquer » apparaissent ici. Ils partent dans le prochain lot « À corriger (Woo) » qui cachera la fiche sur le site. Réactiver les remet dans la file."
                  : view === 'pack'
                    ? "Les produits mis de côté (hors matching et hors export Sage, toujours visibles sur le site) apparaissent ici."
                    : view === 'rejected'
                      ? "Les produits rejetés quittent la file de revue. Scannez le code-barres du produit pour l'approuver directement, ou « Rétablir » pour une nouvelle décision."
                      : "Chaque décision prise dans la file d'attente apparaît ici avec son auteur, son horodatage et la version de l'algorithme."
              }
            />
          )}
          {view === 'hide' && (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <label htmlFor="hide-brand" className="text-xs font-semibold text-ink-soft">
                Marque
              </label>
              <select
                id="hide-brand"
                value={hideBrand}
                onChange={(e) => setHideBrand(e.target.value)}
                className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs font-semibold text-ink shadow-xs"
              >
                <option value="">
                  Toutes les marques
                  {operatorBrandsQuery.data !== undefined ? ` (${operatorBrandsQuery.data.total})` : ''}
                </option>
                {operatorBrandsQuery.data?.brands.map((b) => (
                  <option key={b.brand ?? '__NOBRAND__'} value={b.brand ?? '__NOBRAND__'}>
                    {(b.brand ?? '— Sans marque —') + ` (${b.count})`}
                  </option>
                ))}
              </select>
              {operatorBrandsQuery.data?.truncated === true && (
                <span className="text-[11px] text-ink-faint">comptes tronqués au-delà de 500 marques</span>
              )}
              <span className="flex-1" />
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={visibleDecisions.length === 0 || hideBulkMutation.isPending}
                onClick={() => {
                  const ids = visibleDecisions.map((d) => d.id);
                  if (ids.length > 0) hideBulkMutation.mutate(ids);
                }}
                title="Pousse catalog_visibility=hidden sur WooCommerce pour tous les produits de la page (lot vérifié, chaque fiche est relue)"
              >
                {hideBulkMutation.isPending
                  ? 'Masquage…'
                  : `Tout masquer sur Woo (page) (${visibleDecisions.length})`}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={visibleDecisions.length === 0 || unhideBulkMutation.isPending}
                onClick={() => {
                  const ids = visibleDecisions.map((d) => d.id);
                  if (ids.length > 0) unhideBulkMutation.mutate(ids);
                }}
                title="Pousse catalog_visibility=visible sur WooCommerce pour rétablir les produits dans la boutique et la recherche"
              >
                {unhideBulkMutation.isPending
                  ? 'Rétablissement…'
                  : `Tout rétablir sur Woo (Shop & search) (${visibleDecisions.length})`}
              </Button>
            </div>
          )}
          {view === 'rejected' && (
            <div className="mb-4 space-y-3">
              {/* Primary Toolbar: Search + Brand dropdown with counts + Sort + Limit + Export */}
              <div className="flex flex-wrap items-center justify-between gap-2.5 rounded-xl border border-border bg-card p-3 shadow-2xs">
                {/* Search in rejected */}
                <form
                  className="flex items-center gap-1.5"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setRejectedQuery(rejectedSearch.trim());
                  }}
                >
                  <div className="relative">
                    <input
                      value={rejectedSearch}
                      onChange={(e) => setRejectedSearch(e.target.value)}
                      placeholder="SKU, nom, code-barres…"
                      aria-label="Rechercher dans les rejetés"
                      className="h-8.5 w-48 sm:w-60 rounded-lg border border-border bg-card pl-8 pr-2.5 text-xs text-ink placeholder:text-ink-ghost focus:border-herb-600 focus:ring-2 focus:ring-herb-500/20 outline-none shadow-xs"
                    />
                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-ink-ghost pointer-events-none" />
                  </div>
                  <Button type="submit" variant="primary" size="sm">
                    Chercher
                  </Button>
                  {rejectedQuery !== '' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setRejectedSearch('');
                        setRejectedQuery('');
                      }}
                    >
                      Effacer
                    </Button>
                  )}
                </form>

                <div className="flex flex-wrap items-center gap-2">
                  {/* Brand select with counts */}
                  <div className="flex items-center gap-1.5">
                    <label htmlFor="rejected-brand" className="text-xs font-semibold text-ink-soft whitespace-nowrap">
                      Marque
                    </label>
                    <select
                      id="rejected-brand"
                      value={rejectedBrand}
                      onChange={(e) => setRejectedBrand(e.target.value)}
                      className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs font-semibold text-ink shadow-xs max-w-[220px]"
                    >
                      <option value="">
                        Toutes les marques ({operatorBrandsQuery.data?.total ?? visibleDecisions.length})
                      </option>
                      {operatorBrandsQuery.data?.brands.map((b) => (
                        <option key={b.brand ?? '__NOBRAND__'} value={b.brand ?? '__NOBRAND__'}>
                          {(b.brand ?? '— Sans marque —') + ` (${b.count})`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sort select */}
                  <div className="flex items-center gap-1.5">
                    <label htmlFor="rejected-sort" className="text-xs font-semibold text-ink-soft whitespace-nowrap">
                      Tri
                    </label>
                    <select
                      id="rejected-sort"
                      value={rejectedSort}
                      onChange={(e) => setRejectedSort(e.target.value as 'recent' | 'oldest' | 'name' | 'brand' | 'price')}
                      className="rounded-lg border border-border bg-card px-2 py-1.5 text-xs font-semibold text-ink shadow-xs"
                    >
                      <option value="recent">Plus récents d&apos;abord</option>
                      <option value="oldest">Plus anciens d&apos;abord</option>
                      <option value="name">Nom (A → Z)</option>
                      <option value="brand">Marque (A → Z)</option>
                      <option value="price">Prix d&apos;achat (décroissant)</option>
                    </select>
                  </div>

                  {/* Limit select */}
                  <div className="flex items-center gap-1.5">
                    <label htmlFor="rejected-limit" className="text-xs font-semibold text-ink-soft whitespace-nowrap">
                      Afficher
                    </label>
                    <select
                      id="rejected-limit"
                      value={rejectedLimit}
                      onChange={(e) => setRejectedLimit(Number(e.target.value))}
                      className="rounded-lg border border-border bg-card px-2 py-1.5 text-xs font-semibold text-ink shadow-xs"
                    >
                      <option value={100}>100 produits</option>
                      <option value={300}>300 produits</option>
                      <option value={600}>600 produits</option>
                      <option value={1000}>Tous (jusqu&apos;à 1000)</option>
                    </select>
                  </div>

                  {/* Export CSV button */}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={visibleDecisions.length === 0}
                    onClick={handleExportRejectedCsv}
                    icon={<Download className="h-3.5 w-3.5" />}
                    title="Télécharger la liste des produits rejetés au format CSV pour inventaire / audit"
                  >
                    Exporter CSV ({visibleDecisions.length})
                  </Button>
                </div>
              </div>

              {/* Quick Brand Chips (Top 7 brands with counts for 1-click filtering) */}
              {operatorBrandsQuery.data?.brands && operatorBrandsQuery.data.brands.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 text-xs pt-1">
                  <span className="text-[11px] font-semibold text-ink-ghost mr-1 uppercase tracking-wider">
                    Top Marques :
                  </span>
                  <button
                    type="button"
                    onClick={() => setRejectedBrand('')}
                    className={cn(
                      'rounded-full px-2.5 py-0.5 text-xs font-bold transition-colors border',
                      rejectedBrand === ''
                        ? 'bg-herb-700 text-white border-herb-700 shadow-2xs'
                        : 'bg-card text-ink-soft border-border hover:bg-cream',
                    )}
                  >
                    Toutes ({operatorBrandsQuery.data.total})
                  </button>
                  {operatorBrandsQuery.data.brands
                    .filter((b) => b.brand !== null)
                    .slice(0, 7)
                    .map((b) => {
                      const isActive = rejectedBrand === b.brand;
                      return (
                        <button
                          key={b.brand}
                          type="button"
                          onClick={() => setRejectedBrand(isActive ? '' : (b.brand as string))}
                          className={cn(
                            'rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors border inline-flex items-center gap-1',
                            isActive
                              ? 'bg-herb-700 text-white border-herb-700 font-bold shadow-2xs'
                              : 'bg-card text-ink-soft border-border hover:bg-cream',
                          )}
                        >
                          <span>{b.brand}</span>
                          <span
                            className={cn(
                              'text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold',
                              isActive ? 'bg-white/25 text-white' : 'bg-paper text-ink-ghost',
                            )}
                          >
                            {b.count}
                          </span>
                        </button>
                      );
                    })}
                </div>
              )}

              {/* Bulk Selection & Action Bar */}
              <div className="flex flex-wrap items-center justify-between gap-2.5 rounded-lg border border-border/80 bg-paper/60 px-3 py-2 text-xs">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="select-all-rejected"
                    checked={visibleDecisions.length > 0 && selectedRejectedIds.size === visibleDecisions.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const allIds = new Set<number>();
                        for (const d of visibleDecisions) {
                          const pid = d.productId ?? d.id;
                          allIds.add(pid);
                        }
                        setSelectedRejectedIds(allIds);
                      } else {
                        setSelectedRejectedIds(new Set());
                      }
                    }}
                    className="h-4 w-4 rounded border-border text-herb-600 focus:ring-herb-500 cursor-pointer"
                  />
                  <label htmlFor="select-all-rejected" className="font-semibold text-ink cursor-pointer">
                    {selectedRejectedIds.size > 0
                      ? `${selectedRejectedIds.size} / ${visibleDecisions.length} sélectionné(s)`
                      : `Sélectionner tout sur la page (${visibleDecisions.length})`}
                  </label>
                  {selectedRejectedIds.size > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedRejectedIds(new Set())}
                      className="text-xs text-ink-ghost underline hover:text-ink ml-1 font-medium"
                    >
                      Désélectionner
                    </button>
                  )}
                </div>

                {/* Bulk Action Buttons */}
                <div className="flex flex-wrap items-center gap-2">
                  {selectedRejectedIds.size > 0 ? (
                    <>
                      <Button
                        type="button"
                        variant="primary"
                        size="xs"
                        disabled={reopenBulkMutation.isPending}
                        onClick={() => {
                          setBulkConfirmModal({
                            action: 'reopen',
                            title: `Rétablir ${selectedRejectedIds.size} produit(s)`,
                            message: `Ces ${selectedRejectedIds.size} produit(s) quitteront la liste des rejetés et reviendront dans la file de revue GTIN.`,
                            count: selectedRejectedIds.size,
                            ids: [...selectedRejectedIds],
                          });
                        }}
                        icon={<RotateCw className="h-3 w-3" />}
                        title="Annule le rejet et renvoie les produits sélectionnés vers la file de revue"
                      >
                        {reopenBulkMutation.isPending ? 'Rétablissement…' : `Tout rétablir (${selectedRejectedIds.size})`}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        disabled={hideBulkMutation.isPending}
                        onClick={() => {
                          setBulkConfirmModal({
                            action: 'hide',
                            title: `Masquer ${selectedRejectedIds.size} produit(s) sur WooCommerce`,
                            message: `Ces ${selectedRejectedIds.size} produit(s) seront passés en visibilité cachée (catalog_visibility=hidden) sur WooCommerce.`,
                            count: selectedRejectedIds.size,
                            ids: [...selectedRejectedIds],
                          });
                        }}
                        icon={<EyeOff className="h-3 w-3" />}
                        title="Masque ces produits sur WooCommerce via un lot vérifié"
                      >
                        {hideBulkMutation.isPending ? 'Masquage…' : `Tout masquer (${selectedRejectedIds.size})`}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        disabled={setOperatorStatus.isPending}
                        onClick={() => {
                          setBulkConfirmModal({
                            action: 'pack',
                            title: `Passer ${selectedRejectedIds.size} produit(s) en Pack`,
                            message: `Ces ${selectedRejectedIds.size} produit(s) seront mis de côté en Pack (hors matching et hors export Sage, toujours visibles sur le site).`,
                            count: selectedRejectedIds.size,
                            ids: [...selectedRejectedIds],
                          });
                        }}
                        icon={<Package className="h-3 w-3" />}
                        title="Met ces produits de côté en Pack (hors matching et hors export Sage)"
                      >
                        Passer en Pack ({selectedRejectedIds.size})
                      </Button>
                    </>
                  ) : (
                    <span className="text-[11px] text-ink-ghost italic">
                      Cochez des produits pour effectuer des actions groupées
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="space-y-2">
            {visibleDecisions.map((decision) => (
              <div
                key={decision.id}
                className={cn(
                  'shadow-xs flex flex-wrap items-center gap-3 rounded-xl border bg-card p-3.5 transition-colors',
                  view === 'rejected' && selectedRejectedIds.has(decision.productId ?? decision.id)
                    ? 'border-herb-500 bg-herb-50/20 ring-1 ring-herb-500/30'
                    : 'border-border',
                )}
              >
                {view === 'rejected' && (
                  <input
                    type="checkbox"
                    checked={selectedRejectedIds.has(decision.productId ?? decision.id)}
                    onChange={(e) => {
                      const pid = decision.productId ?? decision.id;
                      setSelectedRejectedIds((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(pid);
                        else next.delete(pid);
                        return next;
                      });
                    }}
                    className="h-4 w-4 rounded border-border text-herb-600 focus:ring-herb-500 cursor-pointer self-center shrink-0"
                    aria-label={`Sélectionner ${decision.product.name}`}
                  />
                )}
                {decision.product.images[0] !== undefined && decision.product.images[0] !== '' ? (
                  <div className="relative group shrink-0">
                    <img
                      src={decision.product.images[0]}
                      alt={decision.product.name}
                      className={cn(
                        'rounded-xl border border-border bg-white shadow-xs transition-all',
                        view === 'pack'
                          ? 'h-36 w-36 sm:h-44 sm:w-44 md:h-48 md:w-48 object-contain p-2 cursor-pointer hover:border-herb-500 hover:ring-2 hover:ring-herb-500/20'
                          : view === 'rejected'
                            ? 'h-28 w-28 sm:h-32 sm:w-32 object-contain p-1.5 cursor-pointer hover:border-herb-500 hover:ring-2 hover:ring-herb-500/20'
                            : 'h-14 w-14 object-cover',
                      )}
                      onClick={() => {
                        if (view === 'rejected' || view === 'pack') {
                          setBarcodeModalTarget({
                            productId: decision.productId ?? decision.id,
                            productName: decision.product.name,
                            sku: decision.product.sku,
                            image: decision.product.images[0] ?? null,
                            currentBarcode: decision.product.barcodeRaw ?? null,
                          });
                        }
                      }}
                      title={view === 'rejected' || view === 'pack' ? 'Cliquer pour scanner / saisir le code-barres' : undefined}
                    />
                    {decision.product.images[0] && (view === 'pack' || view === 'rejected') && (
                      <a
                        href={decision.product.images[0]}
                        target="_blank"
                        rel="noreferrer"
                        className="absolute bottom-1.5 right-1.5 rounded-md bg-white/90 p-1 text-ink-soft hover:text-ink shadow-xs backdrop-blur-xs border border-border opacity-70 group-hover:opacity-100 transition-opacity"
                        title="Ouvrir l'image en grand format"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                ) : (
                  <div
                    className={cn(
                      'rounded-xl border border-border bg-cream flex items-center justify-center text-ink-ghost shrink-0',
                      view === 'pack'
                        ? 'h-36 w-36 sm:h-44 sm:w-44 md:h-48 md:w-48 cursor-pointer'
                        : view === 'rejected'
                          ? 'h-28 w-28 sm:h-32 sm:w-32 cursor-pointer'
                          : 'h-14 w-14',
                    )}
                    onClick={() => {
                      if (view === 'rejected' || view === 'pack') {
                        setBarcodeModalTarget({
                          productId: decision.productId ?? decision.id,
                          productName: decision.product.name,
                          sku: decision.product.sku,
                          image: null,
                          currentBarcode: decision.product.barcodeRaw ?? null,
                        });
                      }
                    }}
                  >
                    <ImageIcon className={view === 'pack' ? 'h-12 w-12 opacity-40' : 'h-6 w-6'} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      'text-sm font-semibold text-ink break-words',
                      (view === 'rejected' || view === 'pack') && 'cursor-pointer hover:text-herb-700 transition-colors',
                    )}
                    onClick={() => {
                      if (view === 'rejected' || view === 'pack') {
                        setBarcodeModalTarget({
                          productId: decision.productId ?? decision.id,
                          productName: decision.product.name,
                          sku: decision.product.sku,
                          image: decision.product.images[0] ?? null,
                          currentBarcode: decision.product.barcodeRaw ?? null,
                        });
                      }
                    }}
                    title={view === 'rejected' || view === 'pack' ? 'Cliquer pour ouvrir le modal scanner' : undefined}
                  >
                    {toCapitalizedCase(decision.product.name)}
                  </p>
                  <p className="flex flex-wrap items-center gap-x-2 font-mono text-xs text-ink-soft mt-0.5">
                    <span>{decision.product.sku ?? '∅'}</span>
                    <span className="text-ink-faint">·</span>
                    <span>{decision.product.brand ?? '—'}</span>
                    <span className="text-ink-faint">·</span>
                    <Gtin gtin={decision.gtin} tone="scan" />
                    {decision.gtin === null && decision.product.barcodeRaw && (
                      <>
                        <span className="text-ink-faint">·</span>
                        <span className="text-ink-ghost text-[11px]" title="Code-barres Woo d'origine">
                          Woo: {decision.product.barcodeRaw}
                        </span>
                      </>
                    )}
                    {decision.product.purchasePriceHt !== null && (
                      <>
                        <span className="text-ink-faint">·</span>
                        <span>achat {money(decision.product.purchasePriceHt)} HT</span>
                      </>
                    )}
                  </p>
                  {decision.note && (
                    <p className="mt-1 flex items-center gap-1 text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5 inline-block" title="Motif / note de rejet">
                      💬 <strong>Note :</strong> {decision.note}
                    </p>
                  )}
                  {view === 'rejected' && decision.bestCandidate && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs bg-emerald-50 text-emerald-900 border border-emerald-300 rounded-lg px-2.5 py-1">
                      <Sparkles className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
                      <span>Candidat disponible : <strong className="font-mono">{decision.bestCandidate.gtin}</strong></span>
                      <span className="text-[10px] text-emerald-700 font-mono">({Math.round(decision.bestCandidate.confidence * 100)}% match)</span>
                      <Button
                        type="button"
                        variant="primary"
                        size="xs"
                        className="h-6 py-0 px-2 text-[11px]"
                        disabled={scanApprove.isPending}
                        onClick={() => {
                          if (decision.productId && decision.bestCandidate?.gtin) {
                            scanApprove.mutate({
                              productId: decision.productId,
                              productName: decision.product.name,
                              code: decision.bestCandidate.gtin,
                            });
                          }
                        }}
                        title={`Approuver directement le candidat ${decision.bestCandidate.gtin}`}
                      >
                        Approuver
                      </Button>
                    </div>
                  )}
                </div>
                <Badge
                  tone={
                    view === 'approved'
                      ? 'good'
                      : view === 'hide'
                        ? 'alarm'
                        : view === 'pack'
                          ? 'signal'
                          : 'muted'
                  }
                >
                  {view === 'approved'
                    ? 'approuvé'
                    : view === 'hide'
                      ? 'masqué'
                      : view === 'pack'
                        ? 'pack'
                        : 'rejeté'}
                </Badge>
                {view === 'hide' && (
                  decision.isVisible === false ? (
                    <Badge tone="good" title="Ce produit est déjà configuré comme masqué / caché sur WooCommerce">
                      ✓ Caché sur Woo
                    </Badge>
                  ) : (
                    <Badge tone="alarm" title="Ce produit est masqué dans l'app mais encore visible sur WooCommerce">
                      Visible sur Woo (à masquer)
                    </Badge>
                  )
                )}
                {view === 'hide' && (
                  <>
                    <Button
                      type="button"
                      variant={decision.isVisible === false ? 'outline' : 'primary'}
                      size="xs"
                      disabled={hideBulkMutation.isPending}
                      onClick={() => hideBulkMutation.mutate([decision.id])}
                      title="Pousse catalog_visibility=hidden sur WooCommerce pour ce produit (lot vérifié, relecture)"
                    >
                      {decision.isVisible === false ? 'Re-masquer sur Woo' : 'Masquer sur Woo'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      disabled={unhideBulkMutation.isPending}
                      onClick={() => unhideBulkMutation.mutate([decision.id])}
                      title="Pousse catalog_visibility=visible sur WooCommerce pour rétablir ce produit sur la boutique et la recherche"
                    >
                      {unhideBulkMutation.isPending ? 'Envoi…' : 'Rétablir sur le site (Shop & search)'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      disabled={setOperatorStatus.isPending}
                      onClick={() => setOperatorStatus.mutate({ productId: decision.id, value: 'none' })}
                      icon={<RotateCw className={cn('h-3.5 w-3.5', setOperatorStatus.isPending && 'animate-spin')} />}
                    >
                      Réactiver
                    </Button>
                  </>
                )}
                {view === 'pack' && (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="primary"
                      size="xs"
                      onClick={() => {
                        setBarcodeModalTarget({
                          productId: decision.productId ?? decision.id,
                          productName: decision.product.name,
                          sku: decision.product.sku,
                          image: decision.product.images[0] ?? null,
                          currentBarcode: decision.product.barcodeRaw ?? null,
                        });
                      }}
                      icon={<ScanLine className="h-3.5 w-3.5" />}
                    >
                      Scanner code-barres
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      disabled={setOperatorStatus.isPending}
                      onClick={() => setOperatorStatus.mutate({ productId: decision.id, value: 'none' })}
                      icon={<RotateCw className={cn('h-3.5 w-3.5', setOperatorStatus.isPending && 'animate-spin')} />}
                    >
                      Retirer du pack
                    </Button>
                  </div>
                )}
                {view === 'rejected' && decision.productId !== undefined && (
                  <>
                    <Button
                      type="button"
                      variant="primary"
                      size="xs"
                      onClick={() => {
                        setBarcodeModalTarget({
                          productId: decision.productId ?? decision.id,
                          productName: decision.product.name,
                          sku: decision.product.sku,
                          image: decision.product.images[0] ?? null,
                          currentBarcode: decision.product.barcodeRaw ?? null,
                        });
                      }}
                      icon={<ScanLine className="h-3.5 w-3.5" />}
                    >
                      Scanner le code
                    </Button>
                    <form
                      className="flex items-center gap-1.5"
                      onSubmit={(event) => {
                        event.preventDefault();
                        const value = scanValues[decision.id] ?? '';
                        if (value.trim() === '') return;
                        scanApprove.mutate({
                          productId: decision.productId as number,
                          productName: decision.product.name,
                          code: value.trim(),
                        });
                      }}
                    >
                      <div className="relative">
                        <input
                          value={scanValues[decision.id] ?? ''}
                          onChange={(event) =>
                            setScanValues((prev) => ({ ...prev, [decision.id]: event.target.value }))
                          }
                          placeholder="Scanner le code-barres…"
                          aria-label={`Code-barres pour ${decision.product.name}`}
                          className="h-8 w-48 rounded-lg border border-border bg-card pl-8 pr-2 font-mono text-xs text-ink placeholder:text-ink-ghost focus:border-herb-600 focus:ring-2 focus:ring-herb-500/20 outline-none"
                        />
                        <ScanLine className="absolute left-2.5 top-2 h-4 w-4 text-ink-ghost pointer-events-none" />
                      </div>
                      <Button
                        type="submit"
                        variant="primary"
                        size="xs"
                        disabled={scanApprove.isPending || (scanValues[decision.id] ?? '').trim() === ''}
                      >
                        Valider
                      </Button>
                    </form>
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      disabled={setOperatorStatus.isPending}
                      title="Mettre de côté : hors matching et hors export Sage, reste visible sur le site"
                      onClick={() => setOperatorStatus.mutate({ productId: decision.productId as number, value: 'pack' })}
                      icon={<Package className="h-3.5 w-3.5" />}
                    >
                      Pack
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      disabled={setOperatorStatus.isPending}
                      title="Masquer du site au prochain envoi Woo + hors export Sage"
                      onClick={() => setOperatorStatus.mutate({ productId: decision.productId as number, value: 'hide' })}
                      icon={<EyeOff className="h-3.5 w-3.5" />}
                    >
                      Masquer
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      disabled={reopen.isPending}
                      onClick={() => reopen.mutate(decision.productId as number)}
                      icon={<RotateCw className={cn('h-3.5 w-3.5', reopen.isPending && 'animate-spin')} />}
                    >
                      Rétablir
                    </Button>
                  </>
                )}
                <span className="font-mono text-[10px] tracking-wider text-ink-faint">
                  <span className="font-semibold text-ink-soft">{decision.decidedBy ?? '—'}</span> · {stamp(decision.decidedAt)}
                  {decision.algoVersion !== 'discontinued' && ` · ${decision.algoVersion.toUpperCase()}`}
                </span>
              </div>
            ))}
          </div>
          {view === 'hide' && visibleDecisions.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-3.5 shadow-2xs">
              <span className="text-xs text-ink-soft">
                {visibleDecisions.length} produit(s) masqué(s) affiché(s) ·{' '}
                <strong className="text-ink">
                  {visibleDecisions.filter((d) => d.isVisible === false).length}
                </strong>{' '}
                déjà caché(s) sur WooCommerce
              </span>
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={visibleDecisions.length === 0 || hideBulkMutation.isPending}
                onClick={() => {
                  const ids = visibleDecisions.map((d) => d.id);
                  if (ids.length > 0) hideBulkMutation.mutate(ids);
                }}
                title="Pousse catalog_visibility=hidden sur WooCommerce pour tous les produits de la page (lot vérifié, chaque fiche est relue)"
              >
                {hideBulkMutation.isPending
                  ? 'Masquage…'
                  : `Tout masquer sur Woo (page) (${visibleDecisions.length})`}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        open={approveOpen}
        title="Approuver toute la page"
        confirmLabel={`Approuver ${approvable.length} produit${approvable.length > 1 ? 's' : ''}`}
        busy={approvePage.isPending}
        onConfirm={() => approvePage.mutate()}
        onCancel={() => setApproveOpen(false)}
      >
        <p className="leading-relaxed text-sm text-ink">
          Le code du <strong>meilleur candidat</strong> de chaque ligne sera approuvé pour{' '}
          <strong>{approvable.length} produit{approvable.length > 1 ? 's' : ''}</strong> de la page {page}. Chaque décision est tracée avec votre nom dans l&apos;onglet Approuvés.
        </p>
        <ul className="mt-3 space-y-1.5 border-t border-border pt-2.5">
          {approvable.slice(0, 8).map((item) => (
            <li
              key={item.productId}
              className="flex flex-wrap items-center gap-2 font-mono text-xs"
            >
              <span className="w-20 shrink-0 text-ink-soft">{item.product.sku ?? '∅'}</span>
              <span className="min-w-0 flex-1 truncate text-ink">{toCapitalizedCase(item.product.name)}</span>
              <span className="text-ink-faint">→</span>
              <span className="tabular font-bold text-herb-700">{item.candidates[0]?.gtinRaw ?? '∅'}</span>
            </li>
          ))}
        </ul>
        {approvable.length > 8 && (
          <p className="mt-2 font-mono text-xs text-ink-faint">
            … et {approvable.length - 8} autres lignes de cette page.
          </p>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={rejectOpen}
        tone="alarm"
        title="Rejeter toute la page"
        confirmLabel={`Rejeter ${items.filter((item) => item.decided === null).length} produits`}
        busy={rejectPage.isPending}
        onConfirm={() => rejectPage.mutate()}
        onCancel={() => setRejectOpen(false)}
      >
        <p className="leading-relaxed text-sm text-ink">
          Tous les produits <strong>non décidés</strong> de la page {page} seront
          rejetés : ils sortent de la file faute de preuve de code-barres fiable. Le rejet est{' '}
          <strong>réversible</strong> — bouton « Rétablir » dans l&apos;onglet Rejetés.
        </p>
        <p className="mt-2 font-mono text-xs text-ink-faint">
          à utiliser pour écarter rapidement les produits sans candidat ou avec des correspondances erronées.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={discontinueTarget !== null}
        tone="alarm"
        title="Masquer un produit"
        confirmLabel="Masquer le produit"
        busy={discontinue.isPending}
        onConfirm={() => {
          if (discontinueTarget !== null) discontinue.mutate(discontinueTarget.id);
        }}
        onCancel={() => setDiscontinueTarget(null)}
      >
        <p className="leading-relaxed text-sm text-ink">
          <strong className="text-ink">{toCapitalizedCase(discontinueTarget?.name ?? '')}</strong> passe
          en statut <strong>masqué</strong> : retiré du matching, de la file de revue et de
          l&apos;export Sage. Il apparaît dans « À corriger (Woo) » et sera <strong>caché sur le
          site</strong> au prochain envoi de lot vérifié.
        </p>
        <p className="mt-2 font-mono text-xs text-ink-faint">
          produit #{discontinueTarget?.id ?? '—'} · journalisé dans l&apos;audit · réversible depuis Masqués
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={executeTarget !== null}
        tone="alarm"
        title="Envoyer vers WooCommerce"
        confirmLabel={`Envoyer ${executeTarget?.changeCount ?? 0} changements (${executeTarget?.env ?? '—'})`}
        busy={executeBatch.isPending}
        onConfirm={() => {
          if (executeTarget !== null) executeBatch.mutate(executeTarget.id);
        }}
        onCancel={() => setExecuteTarget(null)}
      >
        <p className="leading-relaxed text-sm text-ink">
          Le lot <strong>#{executeTarget?.id ?? '—'}</strong> va écraser le champ code-barres de{' '}
          <strong>{executeTarget?.changeCount ?? 0} produits</strong> sur le WooCommerce{' '}
          <strong>{executeTarget?.env ?? '—'}</strong> avec les GTIN approuvés, puis relire chaque
          fiche pour vérifier. Chaque écriture est journalisée (avant → après) et réversible.
        </p>
        <p className="mt-2 font-mono text-xs text-ink-faint">
          cible : {executeTarget?.env ?? '—'} · approuvé par vous · jamais la production depuis ici
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={conflictTarget !== null}
        tone="alarm"
        title="Code GTIN déjà attribué"
        confirmLabel="Transférer le code"
        busy={decide.isPending}
        onConfirm={() => {
          if (conflictTarget !== null) {
            decide.mutate({
              productId: conflictTarget.productId,
              productName: conflictTarget.productName,
              decision: 'approve',
              gtin: conflictTarget.gtin,
              candidateId: conflictTarget.candidateId,
              force: true,
            });
          }
        }}
        onCancel={() => {
          setConflictTarget(null);
          decide.reset();
        }}
      >
        <div className="space-y-3 leading-relaxed text-sm text-ink">
          {decide.isError && (
            <div className="rounded-md border border-rose-300 bg-rose-50 p-2.5 text-xs text-rose-800">
              {decide.error instanceof Error ? decide.error.message : 'Échec de la validation'}
            </div>
          )}
          <p>
            Le code-barres <strong className="font-mono text-signal">{conflictTarget?.gtin}</strong> est
            déjà attribué à un autre produit dans la base :
          </p>
          <div className="rounded-md border border-border bg-card p-3 text-ink">
            <div className="font-medium">
              [{conflictTarget?.currentSku ?? '∅'}] {toCapitalizedCase(conflictTarget?.productName ?? '')}
            </div>
            <div className="text-xs text-ink-soft mt-1">Produit courant (celui que vous validez)</div>
          </div>
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900">
            <div className="font-medium">
              [{conflictTarget?.conflictingProduct.sku ?? 'Sans réf'}] {toCapitalizedCase(conflictTarget?.conflictingProduct.name)}
            </div>
            <div className="text-xs text-amber-700 mt-1">
              ID produit #{conflictTarget?.conflictingProduct.id}
            </div>
          </div>
          <p>
            Voulez-vous transférer ce code au produit actuel (<strong>{toCapitalizedCase(conflictTarget?.productName)}</strong>) ?
          </p>
          <p className="text-xs text-ink-faint">
            L&apos;ancien produit perdra ce code et retournera immédiatement dans la file d&apos;attente pour être révisé.
          </p>
          {conflictTarget?.isVariantSibling && (
            <div className="rounded-lg border border-herb-300 bg-herb-50 p-3 text-herb-900">
              <div className="flex items-center gap-1.5 font-bold text-xs text-herb-800">
                <Sparkles className="h-4 w-4 text-herb-600" />
                Déclinaison de la même gamme détectée
              </div>
              <p className="mt-1 text-xs text-herb-700">
                Ces deux produits partagent la même gamme physique. Vous pouvez valider ce produit comme <strong>variante à code partagé</strong> sans écraser le produit précédent.
              </p>
              <div className="mt-2.5">
                <Button
                  type="button"
                  variant="primary"
                  size="xs"
                  disabled={decide.isPending}
                  onClick={() => {
                    if (conflictTarget !== null) {
                      decide.mutate({
                        productId: conflictTarget.productId,
                        productName: conflictTarget.productName,
                        decision: 'approve',
                        gtin: conflictTarget.gtin,
                        candidateId: conflictTarget.candidateId,
                        allowSharedVariant: true,
                      });
                    }
                  }}
                >
                  {decide.isPending ? 'Validation en cours…' : '✓ Valider comme variante partagée (EAN partagé)'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </ConfirmDialog>

      {/* Variant Picker Dialog for shared-EAN family */}
      {variantPicker !== null && (
        <ConfirmDialog
          open={variantPicker !== null}
          title={`Variantes détectées pour le code ${variantPicker.code}`}
          confirmLabel="Valider cette variante"
          busy={scanApprove.isPending}
          onConfirm={handleConfirmVariantSelection}
          onCancel={() => {
            setVariantPicker(null);
            setSelectedVariantId(null);
          }}
        >
          <div className="space-y-3 leading-relaxed text-sm text-ink">
            <p className="text-xs text-ink-soft">
              Ce code-barres physique (<strong className="font-mono text-signal">{variantPicker.code}</strong>) correspond à{' '}
              <strong>{variantPicker.items.length} déclinaisons de la même gamme</strong>. Cochez la variante physique que vous avez en main :
            </p>
            <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
              {variantPicker.items.map((item) => {
                const isSelected = selectedVariantId === item.id;
                return (
                  <label
                    key={item.id}
                    className={cn(
                      'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                      isSelected
                        ? 'border-herb-600 bg-herb-50/60 ring-2 ring-herb-500/20'
                        : 'border-border bg-card hover:bg-cream/40',
                    )}
                  >
                    <input
                      type="radio"
                      name="variant-select"
                      checked={isSelected}
                      onChange={() => setSelectedVariantId(item.id)}
                      className="h-4 w-4 text-herb-600 focus:ring-herb-500"
                    />
                    {item.image && (
                      <img
                        src={item.image}
                        alt=""
                        className="h-10 w-10 shrink-0 rounded-md border border-border object-cover bg-cream"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-bold text-ink">
                        {toCapitalizedCase(item.name)}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5 font-mono text-[10px] text-ink-soft">
                        <span>{item.sku ?? 'Sans réf'}</span>
                        <span>·</span>
                        <span>{item.brand ?? '—'}</span>
                        {item.reviewStatus === 'approved' && (
                          <Badge tone="good">approuvé ✓</Badge>
                        )}
                        {item.reviewStatus === 'rejected' && (
                          <Badge tone="muted">rejeté</Badge>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <p className="text-[11px] text-ink-faint">
              ℹ️ Valider cette variante n&apos;effacera aucun produit frère. Les autres variantes pourront être validées séparément avec le même code lors de leur passage physique.
            </p>
          </div>
        </ConfirmDialog>
      )}

      {/* Modal Scanner Barcode & Recherche Concurrents pour les produits rejetés / pack */}
      {barcodeModalTarget !== null && (
        <ConfirmDialog
          open={barcodeModalTarget !== null}
          size="2xl"
          title="Scanner ou trouver le code-barres (Concurrents & Douchette)"
          confirmLabel={scanApprove.isPending ? 'Validation…' : 'Valider saisie douchette'}
          busy={scanApprove.isPending}
          onConfirm={() => {
            if (barcodeModalInput.trim() === '') return;
            scanApprove.mutate({
              productId: barcodeModalTarget.productId,
              productName: barcodeModalTarget.productName,
              sku: barcodeModalTarget.sku,
              code: barcodeModalInput.trim(),
            });
          }}
          onCancel={() => {
            setBarcodeModalTarget(null);
            setBarcodeModalInput('');
          }}
        >
          <div className="space-y-4">
            {/* Target Product Summary Card */}
            <div className="flex items-center gap-4 rounded-xl border border-border bg-cream/40 p-3">
              {barcodeModalTarget.image ? (
                <div className="relative group shrink-0">
                  <img
                    src={barcodeModalTarget.image}
                    alt={barcodeModalTarget.productName}
                    className="h-24 w-24 sm:h-28 sm:w-28 rounded-lg border border-border bg-white object-contain p-1.5 shrink-0 shadow-xs"
                  />
                  <a
                    href={barcodeModalTarget.image}
                    target="_blank"
                    rel="noreferrer"
                    className="absolute bottom-1 right-1 rounded-md bg-white/90 p-1 text-ink-soft hover:text-ink shadow-xs backdrop-blur-xs border border-border opacity-70 group-hover:opacity-100 transition-opacity"
                    title="Ouvrir l'image en taille réelle"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              ) : (
                <div className="flex h-24 w-24 sm:h-28 sm:w-28 shrink-0 items-center justify-center rounded-lg border border-border bg-white text-ink-ghost">
                  <ImageIcon className="h-10 w-10 opacity-40" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-bold text-ink leading-snug">{toCapitalizedCase(barcodeModalTarget.productName)}</h4>
                <div className="mt-1 flex flex-wrap items-center gap-2 font-mono text-xs text-ink-soft">
                  <Badge tone="muted">SKU: {barcodeModalTarget.sku ?? '∅'}</Badge>
                  {barcodeModalTarget.currentBarcode && (
                    <span>Woo actuel: {barcodeModalTarget.currentBarcode}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Option 1: Physical Barcode Scanner Gun */}
            <div className="rounded-xl border border-border bg-card p-3 space-y-2">
              <label htmlFor="rejected-scan-modal-input" className="block text-xs font-bold text-ink flex items-center gap-1.5">
                <ScanLine className="h-4 w-4 text-herb-600" />
                Option 1 : Douchette physique ou saisie manuelle (EAN-13)
              </label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <input
                    id="rejected-scan-modal-input"
                    ref={barcodeModalInputRef}
                    type="text"
                    autoFocus
                    value={barcodeModalInput}
                    onChange={(e) => setBarcodeModalInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && barcodeModalInput.trim() !== '') {
                        e.preventDefault();
                        scanApprove.mutate({
                          productId: barcodeModalTarget.productId,
                          productName: barcodeModalTarget.productName,
                          sku: barcodeModalTarget.sku,
                          code: barcodeModalInput.trim(),
                        });
                      }
                    }}
                    placeholder="Pointez la douchette ou tapez: 3401399999999..."
                    className="h-10 w-full rounded-lg border-2 border-herb-600/70 bg-card pl-9 pr-3 font-mono text-sm font-bold text-ink placeholder:text-ink-ghost focus:border-herb-600 focus:outline-none focus:ring-2 focus:ring-herb-500/20"
                  />
                  <ScanLine className="absolute left-2.5 top-2.5 h-5 w-5 text-herb-600" />
                </div>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={barcodeModalInput.trim() === '' || scanApprove.isPending}
                  onClick={() => {
                    if (barcodeModalInput.trim() === '') return;
                    scanApprove.mutate({
                      productId: barcodeModalTarget.productId,
                      productName: barcodeModalTarget.productName,
                      sku: barcodeModalTarget.sku,
                      code: barcodeModalInput.trim(),
                    });
                  }}
                >
                  Valider
                </Button>
              </div>
            </div>

            {/* Option 2: Search Competitor Websites */}
            <div className="rounded-xl border border-border bg-card p-3 space-y-3">
              <div className="flex items-center justify-between">
                <label htmlFor="competitor-search-modal-input" className="text-xs font-bold text-ink flex items-center gap-1.5">
                  <Search className="h-4 w-4 text-clay-600" />
                  Option 2 : Recherche chez les concurrents (Pharmashop, Parashop, MyCare, Eden Pharma…)
                </label>
                {competitorSearchQuery.data !== undefined && (
                  <Badge tone={competitorSearchQuery.data.total > 0 ? 'good' : 'muted'}>
                    {competitorSearchQuery.data.total} code-barres valide{competitorSearchQuery.data.total > 1 ? 's' : ''}
                  </Badge>
                )}
              </div>

              {/* Search query input */}
              <div className="relative">
                <input
                  id="competitor-search-modal-input"
                  type="text"
                  value={competitorSearchInput}
                  onChange={(e) => setCompetitorSearchInput(e.target.value)}
                  placeholder="Rechercher par libellé ou marque chez les concurrents..."
                  className="h-10 w-full rounded-lg border border-border bg-cream/30 pl-9 pr-8 text-xs font-semibold text-ink placeholder:text-ink-soft focus:border-herb-600 focus:bg-card focus:outline-none focus:ring-2 focus:ring-herb-500/20"
                />
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-ink-soft" />
                {competitorSearchInput !== '' && (
                  <button
                    type="button"
                    onClick={() => setCompetitorSearchInput('')}
                    className="absolute right-2.5 top-2.5 text-ink-soft hover:text-ink"
                    title="Effacer"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Competitor Search Results List */}
              <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
                {competitorSearchQuery.isPending && (
                  <div className="flex items-center justify-center gap-2 py-4 text-xs text-ink-soft">
                    <RotateCw className="h-4 w-4 animate-spin text-herb-600" />
                    <span>Recherche en direct parmi les catalogues concurrents…</span>
                  </div>
                )}

                {competitorSearchQuery.isError && (
                  <p className="py-2 text-xs text-rust-600 text-center">
                    Erreur lors de la recherche concurrents.
                  </p>
                )}

                {competitorSearchQuery.data !== undefined && competitorSearchQuery.data.results.length === 0 && (
                  <div className="rounded-lg border border-dashed border-border bg-cream/20 p-4 text-center">
                    <p className="text-xs text-ink-soft font-medium">
                      Aucun produit avec code-barres valide trouvé pour &laquo;&nbsp;{debouncedCompetitorSearch}&nbsp;&raquo;.
                    </p>
                    <p className="text-[11px] text-ink-faint mt-1">
                      Astuce : simplifiez le nom en ne gardant que les mots-clés essentiels ou la marque.
                    </p>
                  </div>
                )}

                {competitorSearchQuery.data?.results.map((offer) => {
                  const sourcePillClass =
                    offer.sourceKey === 'pharma-shop'
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                      : offer.sourceKey === 'parashop'
                        ? 'bg-sky-50 text-sky-800 border-sky-300'
                        : offer.sourceKey === 'mycare'
                          ? 'bg-purple-50 text-purple-800 border-purple-300'
                          : offer.sourceKey === 'eden'
                            ? 'bg-amber-50 text-amber-800 border-amber-300'
                            : offer.sourceKey === 'paraexpert'
                              ? 'bg-rose-50 text-rose-800 border-rose-300'
                              : 'bg-indigo-50 text-indigo-800 border-indigo-300';

                  return (
                    <div
                      key={offer.id}
                      className="group flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-2.5 transition-all hover:border-herb-500 hover:shadow-xs"
                    >
                      {/* Competitor Image */}
                      {offer.image ? (
                        <div className="relative shrink-0">
                          <img
                            src={offer.image}
                            alt=""
                            className="h-14 w-14 rounded-lg border border-border bg-white object-contain p-1"
                          />
                          {offer.url && (
                            <a
                              href={offer.url}
                              target="_blank"
                              rel="noreferrer"
                              className="absolute bottom-0.5 right-0.5 rounded bg-white/90 p-0.5 text-ink-soft hover:text-ink shadow-2xs border border-border"
                              title="Voir sur le site concurrent"
                            >
                              <ExternalLink className="h-2.5 w-2.5" />
                            </a>
                          )}
                        </div>
                      ) : (
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-border bg-cream text-ink-ghost">
                          <ImageIcon className="h-5 w-5 opacity-40" />
                        </div>
                      )}

                      {/* Info & Barcode */}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className={cn('inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold border', sourcePillClass)}>
                            {offer.sourceLabel}
                          </span>
                          {offer.score >= 80 && (
                            <span className="text-[10px] font-semibold text-herb-700 bg-herb-50 px-1 py-0.5 rounded">
                              {offer.score}% match
                            </span>
                          )}
                          {offer.priceTtc !== null && (
                            <span className="font-mono text-xs font-semibold text-ink">
                              {money(offer.priceTtc)} TTC
                            </span>
                          )}
                        </div>
                        <p className="text-xs font-semibold text-ink mt-1 break-words" title={offer.name}>
                          {offer.name}
                        </p>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 font-mono text-xs font-black text-herb-800 bg-herb-50 border border-herb-300 px-2 py-0.5 rounded">
                            <ScanLine className="h-3.5 w-3.5" />
                            {offer.gtin}
                          </span>
                          <span className="text-[11px] text-ink-soft">✓ Code-barres vérifié</span>
                        </div>
                      </div>

                      {/* 1-Click Validate Button */}
                      <div className="shrink-0">
                        <Button
                          type="button"
                          variant="primary"
                          size="xs"
                          disabled={scanApprove.isPending}
                          icon={<CheckCheck className="h-3.5 w-3.5" />}
                          onClick={() => {
                            scanApprove.mutate({
                              productId: barcodeModalTarget.productId,
                              productName: barcodeModalTarget.productName,
                              sku: barcodeModalTarget.sku,
                              code: offer.gtin,
                            });
                          }}
                          title={`Valider immédiatement ${offer.gtin} (${offer.sourceLabel}) et envoyer vers Couche 2`}
                        >
                          Valider ce code
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Workflow Info Footer */}
            <p className="text-[11px] text-ink-faint leading-relaxed">
              ℹ️ <strong>Parcours garanti :</strong> Dès validation d&apos;un code (douchette ou concurrent), le produit est approuvé, envoyé dans <strong>« À corriger (Woo) »</strong> pour mise à jour API, et intègre automatiquement la <strong>Couche 2 (Final EAN Match)</strong> pour le contrôle visuel final avant déblocage sur l&apos;écran S19 (Prix Grossistes & Sage).
            </p>
          </div>
        </ConfirmDialog>
      )}

      {bulkConfirmModal !== null && (
        <ConfirmDialog
          open={true}
          title={bulkConfirmModal.title}
          confirmLabel={
            bulkConfirmModal.action === 'reopen'
              ? `Confirmer le rétablissement (${bulkConfirmModal.count})`
              : bulkConfirmModal.action === 'hide'
                ? `Confirmer le masquage (${bulkConfirmModal.count})`
                : `Confirmer la mise en Pack (${bulkConfirmModal.count})`
          }
          tone={bulkConfirmModal.action === 'hide' ? 'alarm' : 'signal'}
          onConfirm={() => {
            if (bulkConfirmModal.action === 'reopen') {
              reopenBulkMutation.mutate(bulkConfirmModal.ids);
            } else if (bulkConfirmModal.action === 'hide') {
              hideBulkMutation.mutate(bulkConfirmModal.ids);
              setSelectedRejectedIds(new Set());
              setBulkConfirmModal(null);
            } else if (bulkConfirmModal.action === 'pack') {
              (async () => {
                for (const pid of bulkConfirmModal.ids) {
                  await setOperatorStatus.mutateAsync({ productId: pid, value: 'pack' });
                }
                setSelectedRejectedIds(new Set());
                setBulkConfirmModal(null);
              })();
            }
          }}
          onCancel={() => setBulkConfirmModal(null)}
        >
          <div className="space-y-2 text-xs text-ink-soft">
            <p>{bulkConfirmModal.message}</p>
            <p className="text-[11px] text-ink-faint">
              Nombre de produits concernés : <strong className="text-ink">{bulkConfirmModal.count}</strong>
            </p>
          </div>
        </ConfirmDialog>
      )}
    </div>
  );
}
